// src/pages/Toolkit.jsx
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ================================================================== */
/* Shared Constants                                                    */
/* ================================================================== */

const PSH_BY_REGION = {
  'Philippines - Luzon': 4.5,
  'Philippines - Visayas': 4.8,
  'Philippines - Mindanao': 4.6,
  'Saudi Arabia': 6.0,
  'UAE': 5.8,
  'USA - California': 5.5,
  'USA - Texas': 5.0,
  'USA - New York': 4.2,
  'UK - London': 2.8,
  'Germany': 3.0,
  'Australia - Sydney': 4.5,
  'India': 5.0,
  'Custom': 4.5,
};

const SYSTEM_LOSS = 1.3;
const BATTERY_DOD = 0.8;
const BATTERY_EFFICIENCY = 0.9;
const INVERTER_EFFICIENCY = 0.95;
const PANEL_WATT_DEFAULT = 550;

/* Standard copper wire ampacity (NEC 310.16, 30°C, 75°C insulation) */
const WIRE_AMPACITY = {
  '14 AWG': 15,
  '12 AWG': 20,
  '10 AWG': 30,
  '8 AWG': 50,
  '6 AWG': 65,
  '4 AWG': 85,
  '3 AWG': 100,
  '2 AWG': 115,
  '1 AWG': 130,
  '1/0 AWG': 150,
  '2/0 AWG': 175,
  '3/0 AWG': 200,
  '4/0 AWG': 230,
  '250 kcmil': 255,
  '300 kcmil': 285,
  '350 kcmil': 310,
  '400 kcmil': 335,
  '500 kcmil': 380,
};

/* Copper resistance in ohms per 1000 ft (approx, 75°C) */
const WIRE_RESISTANCE_PER_KFT = {
  '14 AWG': 3.07,
  '12 AWG': 1.93,
  '10 AWG': 1.21,
  '8 AWG': 0.764,
  '6 AWG': 0.491,
  '4 AWG': 0.308,
  '3 AWG': 0.245,
  '2 AWG': 0.194,
  '1 AWG': 0.154,
  '1/0 AWG': 0.122,
  '2/0 AWG': 0.0967,
  '3/0 AWG': 0.0766,
  '4/0 AWG': 0.0608,
  '250 kcmil': 0.0515,
  '300 kcmil': 0.0429,
  '350 kcmil': 0.0367,
  '400 kcmil': 0.0321,
  '500 kcmil': 0.0258,
};

const BREAKER_SIZES = [
  15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100,
  110, 125, 150, 175, 200, 225, 250, 300, 350, 400,
];

/* ================================================================== */
/* One-Line Diagram — device library                                   */
/* ================================================================== */

const DIAGRAM_DEVICES = {
  source: {
    label: 'Source / Utility',
    symbol: 'source',
    color: '#2563eb',
    defaults: { voltage: 230, phase: 3 },
  },
  transformer: {
    label: 'Transformer',
    symbol: 'transformer',
    color: '#7c3aed',
    defaults: { voltage: 230, phase: 3, kva: 50 },
  },
  panelboard: {
    label: 'Panelboard',
    symbol: 'panelboard',
    color: '#0d9488',
    defaults: { voltage: 230, phase: 3 },
  },
  breaker: {
    label: 'Circuit Breaker',
    symbol: 'breaker',
    color: '#dc2626',
    defaults: { rating: 20 },
  },
  motor: {
    label: 'Motor',
    symbol: 'motor',
    color: '#0891b2',
    defaults: { hp: 5, voltage: 230, phase: 3, fla: 15.2, pf: 0.85 },
  },
  lighting: {
    label: 'Lighting',
    symbol: 'lighting',
    color: '#eab308',
    defaults: { watts: 1000, voltage: 230, phase: 1, pf: 1.0 },
  },
  receptacle: {
    label: 'Receptacle',
    symbol: 'receptacle',
    color: '#16a34a',
    defaults: { watts: 1800, voltage: 230, phase: 1, pf: 1.0 },
  },
  hvac: {
    label: 'HVAC Load',
    symbol: 'hvac',
    color: '#0284c7',
    defaults: { watts: 3500, voltage: 230, phase: 1, pf: 0.9 },
  },
  generator: {
    label: 'Generator',
    symbol: 'generator',
    color: '#ea580c',
    defaults: { kva: 100, voltage: 230, phase: 3 },
  },
  ups: {
    label: 'UPS / Battery',
    symbol: 'ups',
    color: '#9333ea',
    defaults: { kva: 10, voltage: 230, phase: 1 },
  },
  junction: {
    label: 'Junction',
    symbol: 'junction',
    color: '#6b7280',
    defaults: {},
  },
};

const DIAGRAM_GRID = 20;

/* ================================================================== */
/* Tool Registry                                                       */
/* ================================================================== */

const TOOLS = [
  {
    id: 'solar',
    name: 'Solar Sizing',
    description:
      'Size solar array, battery bank, and inverter based on your loads.',
    icon: 'fa-solar-panel',
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    category: 'Renewable',
  },
  {
    id: 'oneLine',
    name: 'One-Line Diagram',
    description:
      'Draw a one-line diagram, connect devices, and auto-calculate cable size and voltage drop.',
    icon: 'fa-diagram-project',
    color: 'text-rose-500',
    bg: 'bg-rose-500/10',
    category: 'Wiring',
  },
  {
    id: 'voltageDrop',
    name: 'Voltage Drop',
    description:
      'Compute voltage drop and recommend wire size for a given run.',
    icon: 'fa-bolt',
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    category: 'Wiring',
  },
  {
    id: 'cableSizing',
    name: 'Cable / Wire Sizing',
    description:
      'Find the correct conductor size based on ampacity and load.',
    icon: 'fa-plug',
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    category: 'Wiring',
  },
  {
    id: 'breakerSizing',
    name: 'Breaker & Fuse Sizing',
    description:
      'Size OCPD (breaker/fuse) for continuous and non-continuous loads.',
    icon: 'fa-toggle-on',
    color: 'text-rose-500',
    bg: 'bg-rose-500/10',
    category: 'Protection',
  },
  {
    id: 'panelboard',
    name: 'Panelboard Sizing',
    description:
      'Calculate total demand load and main breaker size for a panel.',
    icon: 'fa-table-cells-large',
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    category: 'Distribution',
  },
  {
    id: 'ohmsLaw',
    name: 'Ohm\'s Law & Power',
    description:
      'Solve for V, I, R, or P — enter any two values.',
    icon: 'fa-square-root-variable',
    color: 'text-indigo-500',
    bg: 'bg-indigo-500/10',
    category: 'Fundamentals',
  },
  {
    id: 'motorFla',
    name: 'Motor FLA & Starter',
    description:
      'Estimate full-load amps and recommended breaker for motors.',
    icon: 'fa-gear',
    color: 'text-cyan-500',
    bg: 'bg-cyan-500/10',
    category: 'Motors',
  },
  {
    id: 'powerFactor',
    name: 'Power Factor Correction',
    description:
      'Calculate capacitor kVAR needed to improve power factor.',
    icon: 'fa-wave-square',
    color: 'text-teal-500',
    bg: 'bg-teal-500/10',
    category: 'Power Quality',
  },
];

/* ================================================================== */
/* Toast                                                               */
/* ================================================================== */

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const config =
    {
      success: { bg: 'bg-green-500', icon: 'fa-circle-check' },
      error: { bg: 'bg-red-500', icon: 'fa-circle-exclamation' },
      warning: { bg: 'bg-yellow-500', icon: 'fa-triangle-exclamation' },
      info: { bg: 'bg-blue-500', icon: 'fa-circle-info' },
    }[type] || { bg: 'bg-green-500', icon: 'fa-circle-check' };

  return (
    <motion.div
      role="status"
      aria-live="polite"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 340, damping: 26 }}
      className={`fixed bottom-4 right-4 z-[200] px-4 py-3 rounded-lg shadow-lg text-white flex items-center gap-2 max-w-sm ${config.bg}`}
    >
      <i className={`fas ${config.icon}`} aria-hidden="true" />
      <span className="text-sm font-medium flex-1">{message}</span>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="text-white/80 hover:text-white"
      >
        <i className="fas fa-times text-xs" aria-hidden="true" />
      </button>
    </motion.div>
  );
};

/* ================================================================== */
/* Shared UI                                                           */
/* ================================================================== */

const SectionCard = ({ title, icon, children, action }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5 mb-6">
    <div className="flex items-center justify-between gap-2 mb-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-orange/10 flex items-center justify-center">
          <i className={`fas ${icon} text-orange`} aria-hidden="true" />
        </div>
        <h2 className="text-lg font-bold text-gray-800 dark:text-white">
          {title}
        </h2>
      </div>
      {action}
    </div>
    {children}
  </div>
);

const NumberInput = ({ label, value, onChange, min = 0, step = 1, suffix }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
      {label}
    </label>
    <div className="relative">
      <input
        type="number"
        value={value}
        min={min}
        step={step}
        onChange={(e) =>
          onChange(e.target.value === '' ? '' : Number(e.target.value))
        }
        className="w-full p-2 pr-12 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
          {suffix}
        </span>
      )}
    </div>
  </div>
);

const TextInput = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
      {label}
    </label>
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
    />
  </div>
);

const SelectInput = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
    >
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  </div>
);

const ResultStat = ({ label, value, unit, icon, highlight = false }) => (
  <div
    className={`rounded-xl p-4 ${
      highlight
        ? 'bg-gradient-to-br from-orange to-orange-dark text-white shadow-lg'
        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white'
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <i
        className={`fas ${icon} ${
          highlight ? 'text-white/80' : 'text-orange'
        }`}
        aria-hidden="true"
      />
      <span
        className={`text-xs font-medium ${
          highlight ? 'text-white/90' : 'text-gray-500 dark:text-gray-300'
        }`}
      >
        {label}
      </span>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-2xl font-bold">{value}</span>
      <span className="text-sm opacity-80">{unit}</span>
    </div>
  </div>
);

/* Detailed key-value row */
const DetailRow = ({ label, value, unit, mono = false }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
    <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    <span
      className={`text-xs font-semibold text-gray-800 dark:text-gray-100 ${
        mono ? 'font-mono' : ''
      }`}
    >
      {value}
      {unit ? <span className="ml-1 text-gray-400 font-normal">{unit}</span> : null}
    </span>
  </div>
);

/* Formula reference block */
const FormulaBlock = ({ title, formula, note }) => (
  <div className="mt-4 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
    <div className="text-xs font-bold text-blue-700 dark:text-blue-300 mb-1 flex items-center gap-1">
      <i className="fas fa-square-root-variable" aria-hidden="true" />
      {title || 'Formula'}
    </div>
    <div className="text-xs text-blue-800 dark:text-blue-200 font-mono leading-relaxed whitespace-pre-wrap">
      {formula}
    </div>
    {note && (
      <div className="text-[10px] text-blue-600 dark:text-blue-400 mt-1 italic">
        {note}
      </div>
    )}
  </div>
);

/* Collapsible "Show details" */
const DetailsPanel = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full text-xs font-bold text-gray-600 dark:text-gray-300 hover:text-orange transition"
      >
        <span className="flex items-center gap-1">
          <i className="fas fa-list-ul" aria-hidden="true" />
          {title}
        </span>
        <i
          className={`fas fa-chevron-${open ? 'up' : 'down'} text-[10px]`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-2">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ToolHeader = ({ tool, onBack }) => (
  <div className="flex items-center gap-3 mb-6">
    <button
      type="button"
      onClick={onBack}
      className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition"
      aria-label="Back to toolkit"
    >
      <i className="fas fa-arrow-left text-sm text-gray-600 dark:text-gray-300" aria-hidden="true" />
    </button>
    <div className={`w-10 h-10 rounded-xl ${tool.bg} flex items-center justify-center`}>
      <i className={`fas ${tool.icon} ${tool.color}`} aria-hidden="true" />
    </div>
    <div>
      <h2 className="text-xl font-bold text-gray-800 dark:text-white">
        {tool.name}
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {tool.description}
      </p>
    </div>
  </div>
);

/* Copy button helper */
const CopyButton = ({ getText, showToast, label = 'Copy Summary' }) => (
  <button
    type="button"
    onClick={() => {
      const text = getText();
      navigator.clipboard
        .writeText(text)
        .then(() => showToast('Summary copied!', 'success'))
        .catch(() => showToast('Failed to copy', 'error'));
    }}
    className="w-full bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white py-2.5 rounded-lg text-sm font-semibold transition"
  >
    <i className="fas fa-copy mr-1" aria-hidden="true" /> {label}
  </button>
);

/* ================================================================== */
/* One-Line Diagram tool                                               */
/* ================================================================== */

const snapToGrid = (v) => Math.round(v / DIAGRAM_GRID) * DIAGRAM_GRID;

const computeDeviceLoad = (device) => {
  const v = Number(device.voltage) || 230;
  const phase = Number(device.phase) || 1;
  switch (device.type) {
    case 'motor': {
      const fla = Number(device.fla) || 0;
      const pf = Number(device.pf) || 0.85;
      return { current: fla, pf, watts: Math.sqrt(3) * v * fla * pf };
    }
    case 'lighting':
    case 'receptacle':
    case 'hvac': {
      const w = Number(device.watts) || 0;
      const pf = Number(device.pf) || 1;
      const i = phase === 3 ? w / (Math.sqrt(3) * v * pf) : w / (v * pf);
      return { current: i, pf, watts: w };
    }
    default:
      return { current: 0, pf: 1, watts: 0 };
  }
};

const pickCableSize = (requiredAmps) => {
  for (const size of Object.keys(WIRE_AMPACITY)) {
    if (WIRE_AMPACITY[size] >= requiredAmps) return size;
  }
  return null;
};

const pickBreaker = (amps) => {
  for (const b of BREAKER_SIZES) {
    if (b >= amps) return b;
  }
  return null;
};

const computeDrop = ({ current, lengthM, wireSize, voltage, phase, material }) => {
  const R_per_kft = WIRE_RESISTANCE_PER_KFT[wireSize];
  if (!R_per_kft) return null;
  const R_per_m = (R_per_kft / 1000) / 0.3048;
  const materialFactor = material === 'aluminum' ? 1.6 : 1;
  const R_total = phase === 3
    ? Math.sqrt(3) * R_per_m * lengthM * materialFactor
    : 2 * R_per_m * lengthM * materialFactor;
  const dropV = current * R_total;
  const dropPct = voltage > 0 ? (dropV / voltage) * 100 : 0;
  return { dropV, dropPct, R_total };
};

/* ---- Symbol renderer (IEEE 315 / NECA 100 style) ---- */
const DiagramSymbol = ({ type, color, name }) => {
  const stroke = color;
  const fill = 'white';
  const sw = 2.5;

  const label = (
    <text
      x={0} y={44}
      textAnchor="middle"
      fontSize="10"
      fontWeight="600"
      fill={color}
      style={{ pointerEvents: 'none' }}
    >
      {name}
    </text>
  );

  switch (type) {
    case 'source':
      return (
        <g>
          <circle cx={0} cy={0} r={22} fill={fill} stroke={stroke} strokeWidth={sw} />
          <path d="M -12 0 Q -6 -12 0 0 Q 6 12 12 0" fill="none" stroke={stroke} strokeWidth={2} />
          {label}
        </g>
      );
    case 'generator':
      return (
        <g>
          <circle cx={0} cy={0} r={22} fill={fill} stroke={stroke} strokeWidth={sw} />
          <text x={0} y={6} textAnchor="middle" fontSize="18" fontWeight="bold" fill={stroke}>G</text>
          {label}
        </g>
      );
    case 'transformer':
      return (
        <g>
          <circle cx={-10} cy={0} r={16} fill={fill} stroke={stroke} strokeWidth={sw} />
          <circle cx={10} cy={0} r={16} fill={fill} stroke={stroke} strokeWidth={sw} />
          {label}
        </g>
      );
    case 'panelboard':
      return (
        <g>
          <rect x={-30} y={-20} width={60} height={40} rx={3} fill={fill} stroke={stroke} strokeWidth={sw} />
          <line x1={-20} y1={-20} x2={-20} y2={20} stroke={stroke} strokeWidth={2} />
          <line x1={-20} y1={-8} x2={0} y2={-8} stroke={stroke} strokeWidth={1.5} />
          <line x1={-20} y1={0} x2={0} y2={0} stroke={stroke} strokeWidth={1.5} />
          <line x1={-20} y1={8} x2={0} y2={8} stroke={stroke} strokeWidth={1.5} />
          {label}
        </g>
      );
    case 'breaker':
      return (
        <g>
          <line x1={-18} y1={0} x2={-6} y2={0} stroke={stroke} strokeWidth={sw} />
          <line x1={-6} y1={0} x2={6} y2={-12} stroke={stroke} strokeWidth={sw} />
          <line x1={6} y1={0} x2={18} y2={0} stroke={stroke} strokeWidth={sw} />
          <circle cx={6} cy={0} r={2} fill={stroke} />
          {label}
        </g>
      );
    case 'motor':
      return (
        <g>
          <circle cx={0} cy={0} r={22} fill={fill} stroke={stroke} strokeWidth={sw} />
          <text x={0} y={6} textAnchor="middle" fontSize="18" fontWeight="bold" fill={stroke}>M</text>
          {label}
        </g>
      );
    case 'lighting':
      return (
        <g>
          <circle cx={0} cy={0} r={18} fill={fill} stroke={stroke} strokeWidth={sw} />
          <text x={0} y={5} textAnchor="middle" fontSize="14" fontWeight="bold" fill={stroke}>L</text>
          {label}
        </g>
      );
    case 'receptacle':
      return (
        <g>
          <circle cx={0} cy={0} r={18} fill={fill} stroke={stroke} strokeWidth={sw} />
          <line x1={-8} y1={-4} x2={8} y2={-4} stroke={stroke} strokeWidth={2} />
          <line x1={-8} y1={4} x2={8} y2={4} stroke={stroke} strokeWidth={2} />
          <line x1={-4} y1={-8} x2={-4} y2={-4} stroke={stroke} strokeWidth={1.5} />
          <line x1={4} y1={-8} x2={4} y2={-4} stroke={stroke} strokeWidth={1.5} />
          {label}
        </g>
      );
    case 'hvac':
      return (
        <g>
          <circle cx={0} cy={0} r={20} fill={fill} stroke={stroke} strokeWidth={sw} />
          <text x={0} y={5} textAnchor="middle" fontSize="14" fontWeight="bold" fill={stroke}>H</text>
          {label}
        </g>
      );
    case 'ups':
      return (
        <g>
          <line x1={-18} y1={-6} x2={18} y2={-6} stroke={stroke} strokeWidth={3} />
          <line x1={-10} y1={4} x2={10} y2={4} stroke={stroke} strokeWidth={2} />
          <line x1={-6} y1={14} x2={6} y2={14} stroke={stroke} strokeWidth={1.5} />
          {label}
        </g>
      );
    case 'junction':
      return (
        <g>
          <circle cx={0} cy={0} r={8} fill={color} stroke={color} strokeWidth={2} />
          {label}
        </g>
      );
    default:
      return (
        <g>
          <rect x={-24} y={-16} width={48} height={32} rx={4} fill={fill} stroke={stroke} strokeWidth={sw} />
          <text x={0} y={5} textAnchor="middle" fontSize="12" fill={stroke}>?</text>
          {label}
        </g>
      );
  }
};

/* ---- Palette item ---- */
const DiagramPaletteItem = ({ type }) => {
  const def = DIAGRAM_DEVICES[type];
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/devicetype', type);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className="flex items-center gap-2 p-2 rounded-lg bg-white dark:bg-gray-700 hover:bg-orange/5 border border-gray-200 dark:border-gray-600 cursor-grab active:cursor-grabbing transition select-none"
      title={`Drag "${def.label}" onto the canvas`}
    >
      <div
        className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ background: `${def.color}20` }}
      >
        <svg width="22" height="22" viewBox="-26 -26 52 52">
          <DiagramSymbol type={def.symbol} color={def.color} name="" />
        </svg>
      </div>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-100 truncate">
        {def.label}
      </span>
    </div>
  );
};

/* ---- Device node ---- */
const DiagramDeviceNode = ({
  device, selected, onMouseDown, onStartWire, wiringFrom, onDelete,
}) => {
  const def = DIAGRAM_DEVICES[device.type];
  const isWiringFrom = wiringFrom === device.id;
  const borderColor = selected ? '#ff8c42' : isWiringFrom ? '#10b981' : 'transparent';

  return (
    <g
      transform={`translate(${device.x}, ${device.y})`}
      style={{ cursor: 'grab' }}
      onMouseDown={(e) => onMouseDown(e, device.id)}
    >
      {(selected || isWiringFrom) && (
        <rect
          x={-36} y={-32} width={72} height={72}
          rx={8}
          fill="none"
          stroke={borderColor}
          strokeWidth={2.5}
          strokeDasharray={isWiringFrom ? '5 3' : '0'}
        />
      )}

      <DiagramSymbol type={def.symbol} color={def.color} name={device.name} />

      {/* wire start port */}
      <circle
        cx={36} cy={0} r={6}
        fill={isWiringFrom ? '#10b981' : '#ff8c42'}
        stroke="white"
        strokeWidth={2}
        style={{ cursor: 'crosshair' }}
        onMouseDown={(e) => {
          e.stopPropagation();
          onStartWire(device.id);
        }}
      >
        <title>Drag to connect</title>
      </circle>

      {selected && (
        <g
          transform="translate(32, -36)"
          style={{ cursor: 'pointer' }}
          onMouseDown={(e) => {
            e.stopPropagation();
            onDelete(device.id);
          }}
        >
          <circle r={8} fill="#ef4444" stroke="white" strokeWidth={2} />
          <line x1={-3} y1={-3} x2={3} y2={3} stroke="white" strokeWidth={2} strokeLinecap="round" />
          <line x1={-3} y1={3} x2={3} y2={-3} stroke="white" strokeWidth={2} strokeLinecap="round" />
        </g>
      )}
    </g>
  );
};

/* ---- Device properties panel ---- */
const DiagramDevicePanel = ({ device, onChange, onClose }) => {
  if (!device) return null;
  const def = DIAGRAM_DEVICES[device.type];
  const update = (key, val) => onChange({ ...device, [key]: val });

  return (
    <motion.div
      initial={{ x: 320, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 320, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 320, damping: 30 }}
      className="absolute top-3 right-3 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 z-20 max-h-[calc(100%-1.5rem)] overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center"
            style={{ background: `${def.color}20` }}
          >
            <svg width="18" height="18" viewBox="-26 -26 52 52">
              <DiagramSymbol type={def.symbol} color={def.color} name="" />
            </svg>
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Device</div>
            <div className="text-sm font-bold text-gray-800 dark:text-white">
              {def.label}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
          aria-label="Close"
        >
          <i className="fas fa-times text-xs" aria-hidden="true" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Name
          </label>
          <input
            type="text"
            value={device.name}
            onChange={(e) => update('name', e.target.value)}
            className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
          />
        </div>

        {device.voltage !== undefined && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Voltage (V)
              </label>
              <input
                type="number"
                value={device.voltage}
                onChange={(e) => update('voltage', Number(e.target.value) || 0)}
                className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Phase
              </label>
              <select
                value={device.phase || 1}
                onChange={(e) => update('phase', Number(e.target.value))}
                className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
              >
                <option value={1}>1φ</option>
                <option value={3}>3φ</option>
              </select>
            </div>
          </div>
        )}

        {device.type === 'motor' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  HP
                </label>
                <input
                  type="number"
                  value={device.hp}
                  onChange={(e) => update('hp', Number(e.target.value) || 0)}
                  className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  FLA (A)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={device.fla}
                  onChange={(e) => update('fla', Number(e.target.value) || 0)}
                  className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Power Factor
              </label>
              <input
                type="number"
                step="0.01"
                min={0}
                max={1}
                value={device.pf}
                onChange={(e) => update('pf', Number(e.target.value) || 0)}
                className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
              />
            </div>
          </>
        )}

        {['lighting', 'receptacle', 'hvac'].includes(device.type) && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Watts
              </label>
              <input
                type="number"
                value={device.watts}
                onChange={(e) => update('watts', Number(e.target.value) || 0)}
                className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                PF
              </label>
              <input
                type="number"
                step="0.01"
                min={0}
                max={1}
                value={device.pf}
                onChange={(e) => update('pf', Number(e.target.value) || 0)}
                className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
              />
            </div>
          </div>
        )}

        {['transformer', 'generator', 'ups'].includes(device.type) && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Rating (kVA)
            </label>
            <input
              type="number"
              value={device.kva}
              onChange={(e) => update('kva', Number(e.target.value) || 0)}
              className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
            />
          </div>
        )}

        {device.type === 'breaker' && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Rating (A)
            </label>
            <input
              type="number"
              value={device.rating}
              onChange={(e) => update('rating', Number(e.target.value) || 0)}
              className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

/* ---- Cable properties panel ---- */
const DiagramCablePanel = ({ cable, onChange, onClose }) => {
  if (!cable) return null;
  const update = (key, val) => onChange({ ...cable, [key]: val });

  return (
    <motion.div
      initial={{ x: 320, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 320, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 320, damping: 30 }}
      className="absolute top-3 right-3 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-4 z-20 max-h-[calc(100%-1.5rem)] overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-indigo-500 flex items-center justify-center text-white">
            <i className="fas fa-cable-car text-sm" aria-hidden="true" />
          </div>
          <div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Cable Segment</div>
            <div className="text-sm font-bold text-gray-800 dark:text-white">
              {cable.autoSize ? 'Auto-sized' : cable.wireSize || 'Unset'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
          aria-label="Close"
        >
          <i className="fas fa-times text-xs" aria-hidden="true" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Length (m)
          </label>
          <input
            type="number"
            min={0}
            step={1}
            value={cable.lengthM}
            onChange={(e) => update('lengthM', Number(e.target.value) || 0)}
            className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            id="diagram-autosize"
            type="checkbox"
            checked={cable.autoSize}
            onChange={(e) => update('autoSize', e.target.checked)}
            className="rounded"
          />
          <label htmlFor="diagram-autosize" className="text-xs text-gray-600 dark:text-gray-300 select-none">
            Auto-size conductor
          </label>
        </div>

        {!cable.autoSize && (
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Conductor Size
            </label>
            <select
              value={cable.wireSize}
              onChange={(e) => update('wireSize', e.target.value)}
              className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
            >
              {Object.keys(WIRE_AMPACITY).map((s) => (
                <option key={s} value={s}>
                  {s} ({WIRE_AMPACITY[s]} A)
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Material
          </label>
          <select
            value={cable.material}
            onChange={(e) => update('material', e.target.value)}
            className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
          >
            <option value="copper">Copper</option>
            <option value="aluminum">Aluminum</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Max Voltage Drop (%)
          </label>
          <input
            type="number"
            min={0.5}
            max={10}
            step={0.5}
            value={cable.maxDropPct}
            onChange={(e) => update('maxDropPct', Number(e.target.value) || 3)}
            className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
          />
        </div>
      </div>
    </motion.div>
  );
};

/* ---- One-Line Diagram tool ---- */
const OneLineTool = ({ showToast }) => {
  const svgRef = useRef(null);

  const [devices, setDevices] = useState([]);
  const [cables, setCables] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [selectedCableId, setSelectedCableId] = useState(null);

  const [dragging, setDragging] = useState(null);
  const [wiringFrom, setWiringFrom] = useState(null);
  const [wireCursor, setWireCursor] = useState({ x: 0, y: 0 });

  /* ---- Coordinate conversion ---- */
  const screenToSvg = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const t = pt.matrixTransform(ctm.inverse());
    return { x: t.x, y: t.y };
  }, []);

  /* ---- Drop from palette ---- */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/devicetype');
    if (!type || !DIAGRAM_DEVICES[type]) return;
    const { x, y } = screenToSvg(e.clientX, e.clientY);
    const def = DIAGRAM_DEVICES[type];
    const id = `d${Date.now()}`;
    const count = devices.filter((d) => d.type === type).length + 1;
    setDevices((prev) => [
      ...prev,
      {
        id,
        type,
        x: snapToGrid(x),
        y: snapToGrid(y),
        name: `${def.label.split(' ')[0]}-${count}`,
        ...def.defaults,
      },
    ]);
    setSelectedDeviceId(id);
    setSelectedCableId(null);
  }, [devices, screenToSvg]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  /* ---- Device drag ---- */
  const handleDeviceMouseDown = (e, id) => {
    e.stopPropagation();
    const device = devices.find((d) => d.id === id);
    if (!device) return;
    const { x, y } = screenToSvg(e.clientX, e.clientY);
    setDragging({ id, offsetX: x - device.x, offsetY: y - device.y });
    setSelectedDeviceId(id);
    setSelectedCableId(null);
  };

  /* ---- Wiring ---- */
  const startWire = (fromId) => {
    setWiringFrom(fromId);
    const device = devices.find((d) => d.id === fromId);
    if (device) setWireCursor({ x: device.x, y: device.y });
  };

  const finishWire = (toId) => {
    if (!wiringFrom || wiringFrom === toId) {
      setWiringFrom(null);
      return;
    }
    const exists = cables.some(
      (c) =>
        (c.from === wiringFrom && c.to === toId) ||
        (c.from === toId && c.to === wiringFrom),
    );
    if (exists) {
      showToast('Cable already exists between these devices', 'warning');
      setWiringFrom(null);
      return;
    }
    setCables((prev) => [
      ...prev,
      {
        id: `c${Date.now()}`,
        from: wiringFrom,
        to: toId,
        lengthM: 20,
        autoSize: true,
        wireSize: '12 AWG',
        material: 'copper',
        maxDropPct: 3,
      },
    ]);
    setWiringFrom(null);
    showToast('Cable created', 'success');
  };

  /* ---- Global mouse handlers ---- */
  useEffect(() => {
    const onMove = (e) => {
      if (dragging) {
        const { x, y } = screenToSvg(e.clientX, e.clientY);
        setDevices((prev) =>
          prev.map((d) =>
            d.id === dragging.id
              ? { ...d, x: snapToGrid(x - dragging.offsetX), y: snapToGrid(y - dragging.offsetY) }
              : d,
          ),
        );
      }
      if (wiringFrom) {
        const { x, y } = screenToSvg(e.clientX, e.clientY);
        setWireCursor({ x, y });
      }
    };
    const onUp = () => { if (dragging) setDragging(null); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, wiringFrom, screenToSvg]);

  /* ---- Delete helpers ---- */
  const deleteDevice = (id) => {
    setDevices((prev) => prev.filter((d) => d.id !== id));
    setCables((prev) => prev.filter((c) => c.from !== id && c.to !== id));
    if (selectedDeviceId === id) setSelectedDeviceId(null);
  };

  const deleteCable = (id) => {
    setCables((prev) => prev.filter((c) => c.id !== id));
    if (selectedCableId === id) setSelectedCableId(null);
  };

  /* ---- Keyboard ---- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setWiringFrom(null);
        setSelectedDeviceId(null);
        setSelectedCableId(null);
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && !dragging) {
        if (selectedDeviceId) deleteDevice(selectedDeviceId);
        else if (selectedCableId) deleteCable(selectedCableId);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedDeviceId, selectedCableId, dragging]); // eslint-disable-line

  /* ---- Updaters ---- */
  const updateDevice = (updated) =>
    setDevices((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
  const updateCable = (updated) =>
    setCables((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));

  /* ---- Calculations ---- */
  const calculations = useMemo(() => {
    const deviceMap = Object.fromEntries(devices.map((d) => [d.id, d]));

    const loads = {};
    devices.forEach((d) => { loads[d.id] = computeDeviceLoad(d); });

    const adjacency = {};
    cables.forEach((c) => {
      if (!adjacency[c.from]) adjacency[c.from] = [];
      adjacency[c.from].push(c.to);
    });

    const downstream = (startId, visited = new Set()) => {
      if (visited.has(startId)) return { current: 0, watts: 0 };
      visited.add(startId);
      const local = loads[startId] || { current: 0, watts: 0 };
      let current = local.current || 0;
      let watts = local.watts || 0;
      (adjacency[startId] || []).forEach((next) => {
        const sub = downstream(next, visited);
        current += sub.current;
        watts += sub.watts;
      });
      return { current, watts };
    };

    const cableResults = cables.map((c) => {
      const fromDev = deviceMap[c.from];
      const toDev = deviceMap[c.to];
      const { current: downstreamCurrent } = downstream(c.to);

      const designCurrent = downstreamCurrent * 1.25;
      const voltage = fromDev?.voltage || toDev?.voltage || 230;
      const phase = fromDev?.phase || toDev?.phase || 1;

      let wireSize = c.wireSize;
      if (c.autoSize) {
        const ampacityPick = pickCableSize(designCurrent);
        const sizes = Object.keys(WIRE_AMPACITY);
        const startIdx = sizes.indexOf(ampacityPick);
        let candidate = ampacityPick;
        for (let i = Math.max(0, startIdx); i < sizes.length; i++) {
          const size = sizes[i];
          const vd = computeDrop({
            current: downstreamCurrent,
            lengthM: c.lengthM,
            wireSize: size,
            voltage,
            phase,
            material: c.material,
          });
          if (vd && vd.dropPct <= c.maxDropPct) { candidate = size; break; }
        }
        wireSize = candidate || ampacityPick;
      }

      const breaker = pickBreaker(designCurrent);
      const vd = computeDrop({
        current: downstreamCurrent,
        lengthM: c.lengthM,
        wireSize,
        voltage,
        phase,
        material: c.material,
      });
      const dropPct = vd ? vd.dropPct : 0;
      const pass = vd ? dropPct <= c.maxDropPct : true;

      return {
        id: c.id,
        from: fromDev,
        to: toDev,
        lengthM: c.lengthM,
        downstreamCurrent,
        designCurrent,
        wireSize,
        breaker,
        voltage,
        phase,
        dropV: vd ? vd.dropV : 0,
        dropPct,
        pass,
        maxDropPct: c.maxDropPct,
        material: c.material,
      };
    });

    const totalCurrent = Object.values(loads).reduce((s, l) => s + (l.current || 0), 0);
    const totalWatts = Object.values(loads).reduce((s, l) => s + (l.watts || 0), 0);

    return { cableResults, totalCurrent, totalWatts };
  }, [devices, cables]);

  /* ---- Geometry helpers ---- */
  const cablePath = (cable) => {
    const from = devices.find((d) => d.id === cable.from);
    const to = devices.find((d) => d.id === cable.to);
    if (!from || !to) return '';
    const x1 = from.x + 36, y1 = from.y;
    const x2 = to.x - 30, y2 = to.y;
    const midX = (x1 + x2) / 2;
    return `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`;
  };

  const cableMid = (cable) => {
    const from = devices.find((d) => d.id === cable.from);
    const to = devices.find((d) => d.id === cable.to);
    if (!from || !to) return { x: 0, y: 0 };
    return { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  };

  /* ---- Actions ---- */
  const loadSample = () => {
    setDevices([
      { id: 'src1', type: 'source', x: 120, y: 200, name: 'Utility-1', voltage: 230, phase: 3 },
      { id: 'pnl1', type: 'panelboard', x: 320, y: 200, name: 'MDP', voltage: 230, phase: 3 },
      { id: 'mot1', type: 'motor', x: 560, y: 120, name: 'Motor-1', voltage: 230, phase: 3, hp: 5, fla: 15.2, pf: 0.85 },
      { id: 'lit1', type: 'lighting', x: 560, y: 280, name: 'Lighting-1', voltage: 230, phase: 1, watts: 2000, pf: 1 },
    ]);
    setCables([
      { id: 'c1', from: 'src1', to: 'pnl1', lengthM: 15, autoSize: true, wireSize: '2 AWG', material: 'copper', maxDropPct: 3 },
      { id: 'c2', from: 'pnl1', to: 'mot1', lengthM: 30, autoSize: true, wireSize: '10 AWG', material: 'copper', maxDropPct: 3 },
      { id: 'c3', from: 'pnl1', to: 'lit1', lengthM: 25, autoSize: true, wireSize: '12 AWG', material: 'copper', maxDropPct: 3 },
    ]);
    setSelectedDeviceId(null);
    setSelectedCableId(null);
    showToast('Sample loaded', 'success');
  };

  const clearAll = () => {
    setDevices([]); setCables([]);
    setSelectedDeviceId(null); setSelectedCableId(null); setWiringFrom(null);
    showToast('Diagram cleared', 'info');
  };

  const exportCSV = () => {
    const rows = [
      ['From', 'To', 'Length (m)', 'Conductor', 'Material', 'Design Current (A)', 'Breaker (A)', 'Drop (V)', 'Drop (%)', 'Status'],
      ...calculations.cableResults.map((r) => [
        r.from?.name || '-',
        r.to?.name || '-',
        r.lengthM,
        r.wireSize,
        r.material,
        r.designCurrent.toFixed(2),
        r.breaker,
        r.dropV.toFixed(2),
        r.dropPct.toFixed(2),
        r.pass ? 'PASS' : 'FAIL',
      ]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    navigator.clipboard
      .writeText(csv)
      .then(() => showToast('CSV copied!', 'success'))
      .catch(() => showToast('Copy failed', 'error'));
  };

  const selectedDevice = devices.find((d) => d.id === selectedDeviceId);
  const selectedCable = cables.find((c) => c.id === selectedCableId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
      {/* Palette */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-3 h-fit lg:sticky lg:top-6">
        <h3 className="text-xs font-bold text-gray-800 dark:text-white mb-2 flex items-center gap-2">
          <i className="fas fa-shapes text-orange" aria-hidden="true" />
          Device Symbols
        </h3>
        <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">
          Drag onto the canvas →
        </p>
        <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
          {Object.keys(DIAGRAM_DEVICES).map((type) => (
            <DiagramPaletteItem key={type} type={type} />
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-1.5">
          <button
            type="button"
            onClick={loadSample}
            className="w-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-100 py-1.5 rounded-lg text-[11px] font-semibold transition"
          >
            <i className="fas fa-wand-magic-sparkles mr-1" aria-hidden="true" /> Load Sample
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="w-full bg-red-500 hover:bg-red-600 text-white py-1.5 rounded-lg text-[11px] font-semibold transition"
          >
            <i className="fas fa-trash-alt mr-1" aria-hidden="true" /> Clear All
          </button>
          <button
            type="button"
            onClick={exportCSV}
            className="w-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-100 py-1.5 rounded-lg text-[11px] font-semibold transition"
          >
            <i className="fas fa-file-csv mr-1" aria-hidden="true" /> Export CSV
          </button>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 text-[9px] text-gray-500 dark:text-gray-400 leading-relaxed">
          <p><b>Wire:</b> click orange dot → click target.</p>
          <p><b>Delete:</b> select + Del/Backspace.</p>
        </div>
      </div>

      {/* Canvas + Results */}
      <div className="space-y-4">
        <div
          className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="absolute top-2 left-2 z-10 text-[10px] text-gray-500 dark:text-gray-400 bg-white/80 dark:bg-gray-800/80 backdrop-blur px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700">
            {devices.length} device{devices.length === 1 ? '' : 's'} · {cables.length} cable{cables.length === 1 ? '' : 's'}
            {wiringFrom && (
              <span className="ml-2 text-green-600 dark:text-green-400 font-semibold">· Wiring…</span>
            )}
          </div>

          <svg
            ref={svgRef}
            width="100%"
            height="520"
            viewBox="0 0 900 520"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: 'block', cursor: wiringFrom ? 'crosshair' : 'default' }}
            onClick={() => {
              setSelectedDeviceId(null);
              setSelectedCableId(null);
            }}
          >
            <defs>
              <pattern id="diagram-grid" width={DIAGRAM_GRID} height={DIAGRAM_GRID} patternUnits="userSpaceOnUse">
                <path d={`M ${DIAGRAM_GRID} 0 L 0 0 0 ${DIAGRAM_GRID}`} fill="none" stroke="#e5e7eb" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diagram-grid)" />

            {/* Cables */}
            {cables.map((c) => {
              const result = calculations.cableResults.find((r) => r.id === c.id);
              const isSelected = selectedCableId === c.id;
              const color = !result ? '#94a3b8' : result.pass ? '#10b981' : '#ef4444';
              const path = cablePath(c);
              const mid = cableMid(c);
              const labelText = result ? `${result.wireSize} · ${result.dropPct.toFixed(1)}%` : '';
              return (
                <g key={c.id}>
                  <path
                    d={path}
                    stroke={isSelected ? '#ff8c42' : color}
                    strokeWidth={isSelected ? 4 : 2.5}
                    fill="none"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCableId(c.id);
                      setSelectedDeviceId(null);
                    }}
                  />
                  <g transform={`translate(${mid.x}, ${mid.y})`} style={{ pointerEvents: 'none' }}>
                    <rect x={-52} y={-11} width={104} height={20} rx={6} fill="white" stroke={color} strokeWidth={1} />
                    <text x={0} y={3} textAnchor="middle" fontSize="10" fontWeight="600" fill={color}>
                      {labelText}
                    </text>
                  </g>
                </g>
              );
            })}

            {/* Wiring preview */}
            {wiringFrom && (() => {
              const fromDev = devices.find((d) => d.id === wiringFrom);
              if (!fromDev) return null;
              return (
                <line
                  x1={fromDev.x + 36}
                  y1={fromDev.y}
                  x2={wireCursor.x}
                  y2={wireCursor.y}
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  style={{ pointerEvents: 'none' }}
                />
              );
            })()}

            {/* Devices */}
            {devices.map((d) => (
              <g
                key={d.id}
                onClick={(e) => {
                  if (wiringFrom && wiringFrom !== d.id) {
                    e.stopPropagation();
                    finishWire(d.id);
                  }
                }}
              >
                <DiagramDeviceNode
                  device={d}
                  selected={selectedDeviceId === d.id}
                  onMouseDown={handleDeviceMouseDown}
                  onStartWire={startWire}
                  wiringFrom={wiringFrom}
                  onDelete={deleteDevice}
                />
              </g>
            ))}
          </svg>

          {devices.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <i className="fas fa-diagram-project text-4xl text-gray-300 dark:text-gray-600 mb-2 block" aria-hidden="true" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Drag a device from the palette to begin
                </p>
              </div>
            </div>
          )}

          <AnimatePresence>
            {selectedDevice && !wiringFrom && (
              <DiagramDevicePanel
                device={selectedDevice}
                onChange={updateDevice}
                onClose={() => setSelectedDeviceId(null)}
              />
            )}
            {selectedCable && !wiringFrom && (
              <DiagramCablePanel
                cable={selectedCable}
                onChange={updateCable}
                onClose={() => setSelectedCableId(null)}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Results table */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4">
          <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
            <i className="fas fa-calculator text-orange" aria-hidden="true" />
            Cable Sizing & Voltage Drop
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
              <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400">Devices</div>
              <div className="text-base font-bold text-gray-800 dark:text-white">{devices.length}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
              <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400">Cables</div>
              <div className="text-base font-bold text-gray-800 dark:text-white">{cables.length}</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
              <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400">Total Load</div>
              <div className="text-base font-bold text-gray-800 dark:text-white">
                {(calculations.totalWatts / 1000).toFixed(2)} <span className="text-xs font-normal">kW</span>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2">
              <div className="text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400">Total Current</div>
              <div className="text-base font-bold text-gray-800 dark:text-white">
                {calculations.totalCurrent.toFixed(1)} <span className="text-xs font-normal">A</span>
              </div>
            </div>
          </div>

          {calculations.cableResults.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">
              Connect two devices to see cable sizing results.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left text-[10px] text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-2">From → To</th>
                    <th className="py-2 px-2 text-right">Length</th>
                    <th className="py-2 px-2 text-right">Current</th>
                    <th className="py-2 px-2 text-right">Design</th>
                    <th className="py-2 px-2 text-right">Conductor</th>
                    <th className="py-2 px-2 text-right">Breaker</th>
                    <th className="py-2 px-2 text-right">Drop (V)</th>
                    <th className="py-2 px-2 text-right">Drop (%)</th>
                    <th className="py-2 pl-2 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {calculations.cableResults.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-b border-gray-100 dark:border-gray-700/50 cursor-pointer hover:bg-orange/5 transition ${
                        selectedCableId === r.id ? 'bg-orange/10' : ''
                      }`}
                      onClick={() => {
                        setSelectedCableId(r.id);
                        setSelectedDeviceId(null);
                      }}
                    >
                      <td className="py-1.5 pr-2 font-medium text-gray-800 dark:text-gray-100">
                        {r.from?.name || '?'} → {r.to?.name || '?'}
                      </td>
                      <td className="py-1.5 px-2 text-right">{r.lengthM} m</td>
                      <td className="py-1.5 px-2 text-right">{r.downstreamCurrent.toFixed(2)} A</td>
                      <td className="py-1.5 px-2 text-right">{r.designCurrent.toFixed(2)} A</td>
                      <td className="py-1.5 px-2 text-right font-semibold text-orange">{r.wireSize}</td>
                      <td className="py-1.5 px-2 text-right">{r.breaker} A</td>
                      <td className="py-1.5 px-2 text-right">{r.dropV.toFixed(2)} V</td>
                      <td className="py-1.5 px-2 text-right">{r.dropPct.toFixed(2)} %</td>
                      <td className="py-1.5 pl-2 text-right">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${
                            r.pass
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          }`}
                        >
                          <i className={`fas ${r.pass ? 'fa-check' : 'fa-times'}`} aria-hidden="true" />
                          {r.pass ? 'Pass' : 'Fail'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-3 leading-relaxed">
            <i className="fas fa-info-circle mr-1" aria-hidden="true" />
            Symbols follow IEEE 315 / NECA 100. Calculations use 125% continuous-load factor and NEC 310.16 copper ampacity at 75°C. Verify with a licensed engineer.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool: Solar Sizing                                                  */
/* ================================================================== */

const DEFAULT_LOAD = {
  id: 1,
  name: 'LED Light',
  watts: 10,
  qty: 1,
  hours: 6,
  type: 'ac',
};

const SolarTool = ({ showToast }) => {
  const [loads, setLoads] = useState([{ ...DEFAULT_LOAD }]);
  const [region, setRegion] = useState('Philippines - Luzon');
  const [customPsh, setCustomPsh] = useState(4.5);
  const [panelWatt, setPanelWatt] = useState(PANEL_WATT_DEFAULT);
  const [batteryVoltage, setBatteryVoltage] = useState(48);
  const [batteryUnitAh, setBatteryUnitAh] = useState(100);
  const [autonomyDays, setAutonomyDays] = useState(1);
  const [systemType, setSystemType] = useState('hybrid');

  const psh = region === 'Custom' ? customPsh : PSH_BY_REGION[region];

  const updateLoad = (id, field, value) =>
    setLoads((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    );

  const addLoad = () =>
    setLoads((prev) => [
      ...prev,
      { id: Date.now(), name: '', watts: 0, qty: 1, hours: 1, type: 'ac' },
    ]);

  const removeLoad = (id) =>
    setLoads((prev) =>
      prev.length === 1 ? prev : prev.filter((l) => l.id !== id),
    );

  const resetAll = () => {
    setLoads([{ ...DEFAULT_LOAD }]);
    setRegion('Philippines - Luzon');
    setCustomPsh(4.5);
    setPanelWatt(PANEL_WATT_DEFAULT);
    setBatteryVoltage(48);
    setBatteryUnitAh(100);
    setAutonomyDays(1);
    setSystemType('hybrid');
    showToast('Reset to defaults', 'info');
  };

  const results = useMemo(() => {
    let dailyWh = 0;
    let peakW = 0;
    let acDailyWh = 0;
    let dcDailyWh = 0;
    const perLoad = loads.map((l) => {
      const w = Number(l.watts) || 0;
      const q = Number(l.qty) || 0;
      const h = Number(l.hours) || 0;
      const loadWh = w * q * h;
      dailyWh += loadWh;
      peakW += w * q;
      if (l.type === 'ac') acDailyWh += loadWh;
      else dcDailyWh += loadWh;
      return { ...l, loadWh, totalW: w * q };
    });
    const dailyKwh = dailyWh / 1000;
    const acDerate = INVERTER_EFFICIENCY;
    const arrayWp = psh > 0 ? (dailyWh * SYSTEM_LOSS) / (psh * acDerate) : 0;
    const arrayKw = arrayWp / 1000;
    const panelCount = panelWatt > 0 ? Math.ceil(arrayWp / panelWatt) : 0;
    const actualArrayWp = panelCount * panelWatt;
    const batteryWh =
      systemType === 'gridtie'
        ? 0
        : (dailyWh * autonomyDays) / (BATTERY_DOD * BATTERY_EFFICIENCY);
    const batteryKwh = batteryWh / 1000;
    const batteryAh = batteryVoltage > 0 ? batteryWh / batteryVoltage : 0;
    const batteryCount =
      batteryUnitAh > 0 ? Math.ceil(batteryAh / batteryUnitAh) : 0;
    const actualBatteryAh = batteryCount * batteryUnitAh;
    const actualBatteryWh = actualBatteryAh * batteryVoltage;
    const inverterW = peakW * 1.25;
    const inverterKw = inverterW / 1000;
    const mpptCurrent =
      batteryVoltage > 0 ? (actualArrayWp / batteryVoltage) * 1.25 : 0;
    const dailyGenKwh =
      (actualArrayWp * psh * acDerate) / 1000 / SYSTEM_LOSS;
    const arrayAreaM2 = actualArrayWp / 200;
    const loadFactor = peakW > 0 ? dailyWh / (peakW * 24) : 0;
    const specificYield = arrayKw > 0 ? dailyGenKwh / arrayKw : 0;

    return {
      perLoad, dailyWh, dailyKwh, peakW, acDailyWh, dcDailyWh,
      arrayWp, arrayKw, panelCount, actualArrayWp,
      batteryWh, batteryKwh, batteryAh, batteryCount, actualBatteryAh, actualBatteryWh,
      inverterW, inverterKw, mpptCurrent, dailyGenKwh,
      arrayAreaM2, loadFactor, specificYield,
    };
  }, [loads, psh, panelWatt, batteryVoltage, batteryUnitAh, autonomyDays, systemType]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="System Settings" icon="fa-sliders-h">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectInput
              label="System Type"
              value={systemType}
              onChange={setSystemType}
              options={[
                { value: 'offgrid', label: 'Off-Grid (with battery)' },
                { value: 'hybrid', label: 'Hybrid (battery + grid)' },
                { value: 'gridtie', label: 'Grid-Tie (no battery)' },
              ]}
            />
            <SelectInput
              label="Location / Peak Sun Hours"
              value={region}
              onChange={setRegion}
              options={Object.keys(PSH_BY_REGION).map((r) => ({
                value: r,
                label: r !== 'Custom' ? `${r} (${PSH_BY_REGION[r]} PSH)` : r,
              }))}
            />
            {region === 'Custom' && (
              <NumberInput
                label="Custom Peak Sun Hours"
                value={customPsh}
                onChange={setCustomPsh}
                min={1}
                step={0.1}
                suffix="hrs"
              />
            )}
            <NumberInput label="Panel Wattage" value={panelWatt} onChange={setPanelWatt} min={50} step={10} suffix="W" />
            <NumberInput label="Battery System Voltage" value={batteryVoltage} onChange={setBatteryVoltage} min={12} step={12} suffix="V" />
            <NumberInput label="Battery Unit Capacity" value={batteryUnitAh} onChange={setBatteryUnitAh} min={10} step={10} suffix="Ah" />
            {systemType !== 'gridtie' && (
              <NumberInput label="Days of Autonomy" value={autonomyDays} onChange={setAutonomyDays} min={1} step={1} suffix="day(s)" />
            )}
          </div>
        </SectionCard>

        <SectionCard title="Loads" icon="fa-bolt">
          <div className="space-y-3">
            <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 px-1">
              <div className="col-span-3">Appliance</div>
              <div className="col-span-2">Watts</div>
              <div className="col-span-1">Qty</div>
              <div className="col-span-2">Hours/Day</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2 text-right">Energy</div>
            </div>
            <AnimatePresence initial={false}>
              {loads.map((load, idx) => {
                const loadWh =
                  (Number(load.watts) || 0) *
                  (Number(load.qty) || 0) *
                  (Number(load.hours) || 0);
                return (
                  <motion.div
                    key={load.id}
                    layout
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="grid grid-cols-2 md:grid-cols-12 gap-2 items-end bg-gray-50 dark:bg-gray-700/40 rounded-lg p-2"
                  >
                    <div className="col-span-2 md:col-span-3">
                      <TextInput label="Appliance" value={load.name} onChange={(v) => updateLoad(load.id, 'name', v)} placeholder={`Load ${idx + 1}`} />
                    </div>
                    <div className="md:col-span-2">
                      <NumberInput label="Watts" value={load.watts} onChange={(v) => updateLoad(load.id, 'watts', v)} suffix="W" />
                    </div>
                    <div className="md:col-span-1">
                      <NumberInput label="Qty" value={load.qty} onChange={(v) => updateLoad(load.id, 'qty', v)} min={1} />
                    </div>
                    <div className="md:col-span-2">
                      <NumberInput label="Hours/Day" value={load.hours} onChange={(v) => updateLoad(load.id, 'hours', v)} step={0.5} suffix="h" />
                    </div>
                    <div className="md:col-span-2">
                      <SelectInput label="Type" value={load.type} onChange={(v) => updateLoad(load.id, 'type', v)} options={[{ value: 'ac', label: 'AC' }, { value: 'dc', label: 'DC' }]} />
                    </div>
                    <div className="md:col-span-2 flex items-center justify-between md:justify-end gap-2">
                      <span className="text-xs font-semibold text-orange whitespace-nowrap">
                        {loadWh} Wh
                      </span>
                      <button
                        type="button"
                        onClick={() => removeLoad(load.id)}
                        disabled={loads.length === 1}
                        className="text-red-500 hover:bg-red-500/10 p-1.5 rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label="Remove load"
                      >
                        <i className="fas fa-trash-alt text-xs" aria-hidden="true" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button type="button" onClick={addLoad} className="bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-lg text-sm font-semibold transition">
              <i className="fas fa-plus mr-1" aria-hidden="true" /> Add Load
            </button>
            <button type="button" onClick={resetAll} className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-white px-4 py-2 rounded-lg text-sm font-semibold transition">
              <i className="fas fa-rotate-left mr-1" aria-hidden="true" /> Reset
            </button>
          </div>
        </SectionCard>

        <SectionCard title="Per-Load Breakdown" icon="fa-table-list">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2">Appliance</th>
                  <th className="py-2">Type</th>
                  <th className="py-2 text-right">Unit W</th>
                  <th className="py-2 text-right">Qty</th>
                  <th className="py-2 text-right">Hrs/day</th>
                  <th className="py-2 text-right">Total W</th>
                  <th className="py-2 text-right">Daily Wh</th>
                </tr>
              </thead>
              <tbody>
                {results.perLoad.map((l) => (
                  <tr key={l.id} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-2 font-medium">{l.name || 'Unnamed'}</td>
                    <td className="py-2 uppercase text-gray-500 dark:text-gray-400">{l.type}</td>
                    <td className="py-2 text-right">{l.watts}</td>
                    <td className="py-2 text-right">{l.qty}</td>
                    <td className="py-2 text-right">{l.hours}</td>
                    <td className="py-2 text-right">{l.totalW}</td>
                    <td className="py-2 text-right font-semibold text-orange">{l.loadWh.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-bold text-gray-800 dark:text-white">
                  <td colSpan={5} className="py-2">Total</td>
                  <td className="py-2 text-right">{results.peakW} W</td>
                  <td className="py-2 text-right text-orange">{results.dailyWh.toFixed(0)} Wh</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <i className="fas fa-solar-panel text-orange" aria-hidden="true" /> System Size
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <ResultStat label="Solar Array" value={results.arrayKw.toFixed(2)} unit="kWp" icon="fa-solar-panel" highlight />
              <ResultStat label="Daily Energy" value={results.dailyKwh.toFixed(2)} unit="kWh" icon="fa-bolt" />
              <ResultStat label="Panels" value={results.panelCount} unit={`× ${panelWatt}W`} icon="fa-grip" />
              <ResultStat label="Inverter" value={results.inverterKw.toFixed(2)} unit="kW" icon="fa-plug" />
              {systemType !== 'gridtie' && (
                <>
                  <ResultStat label="Battery Bank" value={results.batteryKwh.toFixed(2)} unit="kWh" icon="fa-car-battery" highlight />
                  <ResultStat label="Battery" value={results.batteryCount} unit={`× ${batteryUnitAh}Ah`} icon="fa-battery-full" />
                </>
              )}
              <ResultStat label="MPPT" value={results.mpptCurrent.toFixed(1)} unit="A" icon="fa-microchip" />
            </div>

            <DetailsPanel title="Full Calculation Details">
              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-2 mb-1">Energy</div>
              <DetailRow label="Total daily" value={results.dailyWh.toFixed(0)} unit="Wh" />
              <DetailRow label="AC portion" value={results.acDailyWh.toFixed(0)} unit="Wh" />
              <DetailRow label="DC portion" value={results.dcDailyWh.toFixed(0)} unit="Wh" />
              <DetailRow label="Peak load" value={results.peakW} unit="W" />
              <DetailRow label="Load factor" value={(results.loadFactor * 100).toFixed(1)} unit="%" />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Array</div>
              <DetailRow label="Required" value={results.arrayWp.toFixed(0)} unit="Wp" />
              <DetailRow label="Installed" value={results.actualArrayWp} unit="Wp" />
              <DetailRow label="Est. area" value={results.arrayAreaM2.toFixed(1)} unit="m²" />
              <DetailRow label="Specific yield" value={results.specificYield.toFixed(2)} unit="kWh/kWp/d" />
              <DetailRow label="Est. generation" value={results.dailyGenKwh.toFixed(2)} unit="kWh/day" />

              {systemType !== 'gridtie' && (
                <>
                  <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Battery</div>
                  <DetailRow label="Required capacity" value={results.batteryWh.toFixed(0)} unit="Wh" />
                  <DetailRow label="Actual capacity" value={results.actualBatteryWh.toFixed(0)} unit="Wh" />
                  <DetailRow label="Required Ah" value={results.batteryAh.toFixed(0)} unit="Ah" />
                  <DetailRow label="Actual Ah" value={results.actualBatteryAh.toFixed(0)} unit="Ah" />
                  <DetailRow label="Autonomy" value={autonomyDays} unit="day(s)" />
                </>
              )}

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Equipment</div>
              <DetailRow label="Inverter (125%)" value={results.inverterW.toFixed(0)} unit="W" />
              <DetailRow label="MPPT controller" value={results.mpptCurrent.toFixed(1)} unit="A" />
              <DetailRow label="System losses" value={(SYSTEM_LOSS * 100 - 100).toFixed(0)} unit="%" />
              <DetailRow label="Inverter eff." value={(INVERTER_EFFICIENCY * 100).toFixed(0)} unit="%" />
              <DetailRow label="Battery DoD" value={(BATTERY_DOD * 100).toFixed(0)} unit="%" />
              <DetailRow label="Battery eff." value={(BATTERY_EFFICIENCY * 100).toFixed(0)} unit="%" />
            </DetailsPanel>

            <FormulaBlock
              title="Array sizing"
              formula={`Array (Wp) = (Daily Wh × System Loss) ÷ (PSH × η_inv)\n           = (${results.dailyWh.toFixed(0)} × ${SYSTEM_LOSS}) ÷ (${psh} × ${INVERTER_EFFICIENCY})\n           = ${results.arrayWp.toFixed(0)} Wp`}
            />
            {systemType !== 'gridtie' && (
              <FormulaBlock
                title="Battery sizing"
                formula={`Battery (Wh) = (Daily Wh × Autonomy) ÷ (DoD × η_batt)\n             = (${results.dailyWh.toFixed(0)} × ${autonomyDays}) ÷ (${BATTERY_DOD} × ${BATTERY_EFFICIENCY})\n             = ${results.batteryWh.toFixed(0)} Wh`}
              />
            )}
          </div>

          <CopyButton getText={() => {
            return [
              '=== Solar System Sizing Summary ===',
              `System Type: ${systemType}`,
              `Region: ${region} (${psh} PSH)`,
              `Daily Energy: ${results.dailyKwh.toFixed(2)} kWh`,
              `Peak Load: ${results.peakW} W`,
              `Solar Array: ${results.arrayKw.toFixed(2)} kWp (${results.panelCount} × ${panelWatt}W)`,
              `Inverter: ${results.inverterKw.toFixed(2)} kW`,
              systemType !== 'gridtie'
                ? `Battery: ${results.batteryKwh.toFixed(2)} kWh (${results.batteryCount} × ${batteryUnitAh}Ah @ ${batteryVoltage}V)`
                : 'Battery: N/A (grid-tie)',
              `MPPT: ${results.mpptCurrent.toFixed(1)} A`,
            ].join('\n');
          }} showToast={showToast} />
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool: Voltage Drop                                                  */
/* ================================================================== */

const VoltageDropTool = ({ showToast }) => {
  const [voltage, setVoltage] = useState(230);
  const [current, setCurrent] = useState(20);
  const [lengthM, setLengthM] = useState(30);
  const [wireSize, setWireSize] = useState('10 AWG');
  const [phase, setPhase] = useState('1');
  const [material, setMaterial] = useState('copper');
  const [maxDropPct, setMaxDropPct] = useState(3);

  const result = useMemo(() => {
    const R_per_kft = WIRE_RESISTANCE_PER_KFT[wireSize] || 0;
    const R_per_m = (R_per_kft / 1000) / 0.3048;
    const materialFactor = material === 'aluminum' ? 1.6 : 1;
    const R_total =
      phase === '1'
        ? 2 * R_per_m * lengthM * materialFactor
        : Math.sqrt(3) * R_per_m * lengthM * materialFactor;
    const dropVolts = current * R_total;
    const dropPct = voltage > 0 ? (dropVolts / voltage) * 100 : 0;
    const dropLimit = voltage * (maxDropPct / 100);
    const pass = dropVolts <= dropLimit;
    const powerLoss = current * current * R_total;

    let recommended = null;
    let recommendedDrop = null;
    for (const size of Object.keys(WIRE_RESISTANCE_PER_KFT)) {
      const r = (WIRE_RESISTANCE_PER_KFT[size] / 1000) / 0.3048;
      const rt =
        phase === '1'
          ? 2 * r * lengthM * materialFactor
          : Math.sqrt(3) * r * lengthM * materialFactor;
      const dv = current * rt;
      if (dv <= dropLimit) {
        recommended = size;
        recommendedDrop = dv;
        break;
      }
    }

    return {
      dropVolts, dropPct, dropLimit, pass, recommended, recommendedDrop,
      R_total, R_per_m: R_per_m * materialFactor, R_per_kft: R_per_kft * materialFactor,
      powerLoss,
    };
  }, [voltage, current, lengthM, wireSize, phase, material, maxDropPct]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Circuit Details" icon="fa-sliders-h">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberInput label="System Voltage" value={voltage} onChange={setVoltage} min={1} suffix="V" />
            <NumberInput label="Load Current" value={current} onChange={setCurrent} min={0} step={0.5} suffix="A" />
            <NumberInput label="One-way Length" value={lengthM} onChange={setLengthM} min={0} step={1} suffix="m" />
            <SelectInput label="Conductor Size" value={wireSize} onChange={setWireSize} options={Object.keys(WIRE_RESISTANCE_PER_KFT)} />
            <SelectInput label="Phase" value={phase} onChange={setPhase} options={[{ value: '1', label: 'Single-phase' }, { value: '3', label: 'Three-phase' }]} />
            <SelectInput label="Material" value={material} onChange={setMaterial} options={[{ value: 'copper', label: 'Copper' }, { value: 'aluminum', label: 'Aluminum' }]} />
            <NumberInput label="Max Allowable Drop" value={maxDropPct} onChange={setMaxDropPct} min={0.5} step={0.5} suffix="%" />
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <i className="fas fa-bolt text-orange" aria-hidden="true" /> Result
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <ResultStat label="Voltage Drop" value={result.dropVolts.toFixed(2)} unit="V" icon="fa-arrow-down" highlight />
              <ResultStat label="Drop %" value={result.dropPct.toFixed(2)} unit="%" icon="fa-percent" />
              <ResultStat label="Loop Resistance" value={result.R_total.toFixed(4)} unit="Ω" icon="fa-resistance" />
              <ResultStat label="Limit" value={result.dropLimit.toFixed(2)} unit="V" icon="fa-gauge" />
            </div>
            <div className={`mt-4 p-3 rounded-xl text-sm font-semibold flex items-center gap-2 ${
              result.pass
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
            }`}>
              <i className={`fas ${result.pass ? 'fa-circle-check' : 'fa-triangle-exclamation'}`} aria-hidden="true" />
              {result.pass
                ? `Pass — within ${maxDropPct}% limit`
                : `Fail — exceeds ${maxDropPct}% limit`}
            </div>
            {!result.pass && result.recommended && (
              <p className="mt-3 text-xs text-gray-600 dark:text-gray-300">
                Recommended minimum:{' '}
                <span className="font-bold text-orange">{result.recommended}</span>
                {' '}(drop: {result.recommendedDrop?.toFixed(2)} V)
              </p>
            )}

            <DetailsPanel title="Full Calculation Details">
              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Conductor</div>
              <DetailRow label="Size" value={wireSize} />
              <DetailRow label="Material" value={material} />
              <DetailRow label="R per kft" value={result.R_per_kft.toFixed(4)} unit="Ω/kft" />
              <DetailRow label="R per meter" value={result.R_per_m.toFixed(6)} unit="Ω/m" />
              <DetailRow label="Loop resistance" value={result.R_total.toFixed(4)} unit="Ω" />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Drop</div>
              <DetailRow label="Voltage drop" value={result.dropVolts.toFixed(3)} unit="V" />
              <DetailRow label="Percentage" value={result.dropPct.toFixed(3)} unit="%" />
              <DetailRow label="Limit" value={result.dropLimit.toFixed(3)} unit="V" />
              <DetailRow label="Power loss" value={result.powerLoss.toFixed(2)} unit="W" />
              <DetailRow label="Current" value={current} unit="A" />
              <DetailRow label="Length (1-way)" value={lengthM} unit="m" />
              <DetailRow label="Phase" value={phase === '1' ? 'Single-phase' : 'Three-phase'} />
            </DetailsPanel>

            <FormulaBlock
              title={phase === '1' ? 'Single-phase formula' : 'Three-phase formula'}
              formula={
                phase === '1'
                  ? `V_drop = 2 × I × R × L\n       = 2 × ${current} × ${result.R_per_m.toFixed(6)} × ${lengthM}\n       = ${result.dropVolts.toFixed(3)} V`
                  : `V_drop = √3 × I × R × L\n       = 1.732 × ${current} × ${result.R_per_m.toFixed(6)} × ${lengthM}\n       = ${result.dropVolts.toFixed(3)} V`
              }
              note="Aluminum resistance × 1.6 of copper"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool: Cable / Wire Sizing                                           */
/* ================================================================== */

const CableSizingTool = ({ showToast }) => {
  const [current, setCurrent] = useState(30);
  const [continuous, setContinuous] = useState(true);
  const [ambientC, setAmbientC] = useState(30);
  const [conductors, setConductors] = useState(3);
  const [insulation, setInsulation] = useState('75');
  const [voltage, setVoltage] = useState(230);

  const result = useMemo(() => {
    const designCurrent = current * (continuous ? 1.25 : 1.0);

    let tempFactor = 1.0;
    if (ambientC > 30) {
      if (ambientC <= 35) tempFactor = 0.94;
      else if (ambientC <= 40) tempFactor = 0.88;
      else if (ambientC <= 45) tempFactor = 0.82;
      else if (ambientC <= 50) tempFactor = 0.75;
      else if (ambientC <= 55) tempFactor = 0.67;
      else tempFactor = 0.58;
    }

    let bundleFactor = 1.0;
    if (conductors >= 4 && conductors <= 6) bundleFactor = 0.8;
    else if (conductors >= 7 && conductors <= 9) bundleFactor = 0.7;
    else if (conductors >= 10 && conductors <= 20) bundleFactor = 0.5;
    else if (conductors > 20) bundleFactor = 0.45;

    const requiredAmpacity = designCurrent / (tempFactor * bundleFactor);

    let recommended = null;
    let recommendedAmpacity = 0;
    for (const size of Object.keys(WIRE_AMPACITY)) {
      if (WIRE_AMPACITY[size] >= requiredAmpacity) {
        recommended = size;
        recommendedAmpacity = WIRE_AMPACITY[size];
        break;
      }
    }

    const breaker = BREAKER_SIZES.find((b) => b >= designCurrent) || null;

    const sizes = Object.keys(WIRE_AMPACITY);
    const recommendedIndex = recommended ? sizes.indexOf(recommended) : -1;
    const alternatives = recommendedIndex >= 0
      ? sizes.slice(recommendedIndex, recommendedIndex + 3).map((s) => ({ size: s, ampacity: WIRE_AMPACITY[s] }))
      : [];

    const deratedAmpacity = recommendedAmpacity * tempFactor * bundleFactor;
    const loadKw1ph = (voltage * current) / 1000;
    const loadKw3ph = (Math.sqrt(3) * voltage * current) / 1000;

    return {
      designCurrent, tempFactor, bundleFactor, requiredAmpacity,
      recommended, recommendedAmpacity, breaker, alternatives,
      deratedAmpacity, loadKw1ph, loadKw3ph,
    };
  }, [current, continuous, ambientC, conductors, insulation, voltage]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Load & Environment" icon="fa-sliders-h">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberInput label="Load Current" value={current} onChange={setCurrent} min={0} step={0.5} suffix="A" />
            <NumberInput label="System Voltage" value={voltage} onChange={setVoltage} min={1} suffix="V" />
            <SelectInput label="Load Type" value={continuous ? 'y' : 'n'} onChange={(v) => setContinuous(v === 'y')} options={[{ value: 'y', label: 'Continuous (≥3 hrs) — 125%' }, { value: 'n', label: 'Non-continuous' }]} />
            <NumberInput label="Ambient Temperature" value={ambientC} onChange={setAmbientC} min={10} step={1} suffix="°C" />
            <NumberInput label="Current-Carrying Conductors" value={conductors} onChange={setConductors} min={1} step={1} />
            <SelectInput label="Insulation Rating" value={insulation} onChange={setInsulation} options={[{ value: '60', label: '60°C (TW)' }, { value: '75', label: '75°C (THW, RHW)' }, { value: '90', label: '90°C (THHN, XHHW)' }]} />
          </div>
        </SectionCard>

        <SectionCard title="Candidate Conductors" icon="fa-list-check">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2">Size</th>
                  <th className="py-2 text-right">Ampacity</th>
                  <th className="py-2 text-right">Derated</th>
                  <th className="py-2 text-right">Meets Requirement?</th>
                </tr>
              </thead>
              <tbody>
                {result.alternatives.map((alt) => {
                  const derated = alt.ampacity * result.tempFactor * result.bundleFactor;
                  const meets = derated >= result.designCurrent;
                  return (
                    <tr key={alt.size} className="border-b border-gray-100 dark:border-gray-700/50">
                      <td className="py-2 font-semibold">{alt.size}</td>
                      <td className="py-2 text-right">{alt.ampacity} A</td>
                      <td className="py-2 text-right">{derated.toFixed(1)} A</td>
                      <td className={`py-2 text-right font-semibold ${meets ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                        {meets ? '✓ Yes' : '✗ No'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <i className="fas fa-plug text-orange" aria-hidden="true" /> Result
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <ResultStat label="Design Current" value={result.designCurrent.toFixed(1)} unit="A" icon="fa-bolt" />
              <ResultStat label="Required Ampacity" value={result.requiredAmpacity.toFixed(1)} unit="A" icon="fa-gauge-high" />
              <ResultStat label="Temp Factor" value={result.tempFactor} unit="×" icon="fa-temperature-half" />
              <ResultStat label="Bundle Factor" value={result.bundleFactor} unit="×" icon="fa-layer-group" />
            </div>
            <div className="mt-4 p-3 rounded-xl bg-orange/10 border border-orange/20 text-sm">
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">
                Recommended Conductor
              </div>
              <div className="text-2xl font-bold text-orange">
                {result.recommended || 'N/A'}
              </div>
              {result.recommendedAmpacity > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Rated for {result.recommendedAmpacity} A
                </div>
              )}
              {result.recommendedAmpacity > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Derated: {result.deratedAmpacity.toFixed(1)} A
                </div>
              )}
              {result.breaker && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Suggested breaker:{' '}
                  <span className="font-bold text-gray-700 dark:text-gray-200">
                    {result.breaker} A
                  </span>
                </div>
              )}
            </div>

            <DetailsPanel title="Full Calculation Details">
              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Inputs</div>
              <DetailRow label="Base current" value={current} unit="A" />
              <DetailRow label="Continuous" value={continuous ? 'Yes (×1.25)' : 'No (×1.0)'} />
              <DetailRow label="Ambient" value={ambientC} unit="°C" />
              <DetailRow label="Conductors" value={conductors} />
              <DetailRow label="Insulation" value={`${insulation}°C`} />
              <DetailRow label="Voltage" value={voltage} unit="V" />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Derating</div>
              <DetailRow label="Temp correction" value={result.tempFactor.toFixed(2)} unit="×" />
              <DetailRow label="Bundle adjustment" value={result.bundleFactor.toFixed(2)} unit="×" />
              <DetailRow label="Combined factor" value={(result.tempFactor * result.bundleFactor).toFixed(3)} unit="×" />
              <DetailRow label="Design current" value={result.designCurrent.toFixed(2)} unit="A" />
              <DetailRow label="Required ampacity" value={result.requiredAmpacity.toFixed(2)} unit="A" />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Load</div>
              <DetailRow label="Load (1-ph)" value={result.loadKw1ph.toFixed(2)} unit="kW" />
              <DetailRow label="Load (3-ph)" value={result.loadKw3ph.toFixed(2)} unit="kW" />
            </DetailsPanel>

            <FormulaBlock
              title="Required ampacity"
              formula={`I_req = I_design ÷ (F_temp × F_bundle)\n      = ${result.designCurrent.toFixed(1)} ÷ (${result.tempFactor} × ${result.bundleFactor})\n      = ${result.requiredAmpacity.toFixed(1)} A`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool: Breaker & Fuse Sizing                                         */
/* ================================================================== */

const BreakerSizingTool = ({ showToast }) => {
  const [loadType, setLoadType] = useState('continuous');
  const [current, setCurrent] = useState(20);
  const [voltage, setVoltage] = useState(230);
  const [phase, setPhase] = useState('1');

  const result = useMemo(() => {
    const mult = loadType === 'continuous' ? 1.25 : 1.0;
    const designCurrent = current * mult;
    const breaker = BREAKER_SIZES.find((b) => b >= designCurrent) || null;
    const watts1ph = voltage * current;
    const watts3ph = Math.sqrt(3) * voltage * current;
    const loadWatts = phase === '1' ? watts1ph : watts3ph;
    const utilization = breaker ? (current / breaker) * 100 : 0;
    const suggestedWireIndex = Object.keys(WIRE_AMPACITY).findIndex(
      (s) => WIRE_AMPACITY[s] >= designCurrent,
    );
    const suggestedWire = suggestedWireIndex >= 0
      ? Object.keys(WIRE_AMPACITY)[suggestedWireIndex]
      : null;
    return {
      designCurrent, breaker, loadWatts, utilization, suggestedWire,
      multiplier: mult, watts1ph, watts3ph,
    };
  }, [loadType, current, voltage, phase]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Circuit Details" icon="fa-toggle-on">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectInput label="Load Type" value={loadType} onChange={setLoadType} options={[{ value: 'continuous', label: 'Continuous (×1.25)' }, { value: 'noncontinuous', label: 'Non-continuous (×1.0)' }]} />
            <NumberInput label="Full Load Current" value={current} onChange={setCurrent} min={0} step={0.5} suffix="A" />
            <NumberInput label="Voltage" value={voltage} onChange={setVoltage} min={1} suffix="V" />
            <SelectInput label="Phase" value={phase} onChange={setPhase} options={[{ value: '1', label: 'Single-phase' }, { value: '3', label: 'Three-phase' }]} />
          </div>
        </SectionCard>

        <SectionCard title="Standard Breaker Sizes" icon="fa-list-ol">
          <div className="flex flex-wrap gap-2">
            {BREAKER_SIZES.map((b) => (
              <span
                key={b}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                  b === result.breaker
                    ? 'bg-orange text-white shadow'
                    : b < result.designCurrent
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                {b} A
              </span>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <i className="fas fa-toggle-on text-orange" aria-hidden="true" /> Result
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <ResultStat label="Design Current" value={result.designCurrent.toFixed(1)} unit="A" icon="fa-bolt" highlight />
              <ResultStat label="Load Power" value={(result.loadWatts / 1000).toFixed(2)} unit="kW" icon="fa-plug" />
            </div>
            <div className="mt-4 p-3 rounded-xl bg-orange/10 border border-orange/20 text-sm">
              <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">
                Recommended OCPD
              </div>
              <div className="text-2xl font-bold text-orange">
                {result.breaker ? `${result.breaker} A` : 'N/A'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Standard-size breaker or fuse
              </div>
            </div>

            <DetailsPanel title="Full Calculation Details">
              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Sizing</div>
              <DetailRow label="Load type" value={loadType} />
              <DetailRow label="Multiplier" value={result.multiplier.toFixed(2)} unit="×" />
              <DetailRow label="FLC" value={current} unit="A" />
              <DetailRow label="Design current" value={result.designCurrent.toFixed(2)} unit="A" />
              <DetailRow label="Breaker" value={result.breaker || 'N/A'} unit="A" />
              <DetailRow label="Utilization" value={result.utilization.toFixed(1)} unit="%" />
              <DetailRow label="Min wire size" value={result.suggestedWire || 'N/A'} />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Power</div>
              <DetailRow label="Load (1-ph)" value={(result.watts1ph / 1000).toFixed(3)} unit="kW" />
              <DetailRow label="Load (3-ph)" value={(result.watts3ph / 1000).toFixed(3)} unit="kW" />
              <DetailRow label="Phase selected" value={phase === '1' ? '1-phase' : '3-phase'} />
            </DetailsPanel>

            <FormulaBlock
              title="Design current"
              formula={`I_design = I_FLC × multiplier\n         = ${current} × ${result.multiplier}\n         = ${result.designCurrent.toFixed(2)} A`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool: Panelboard Sizing                                             */
/* ================================================================== */

const PanelboardTool = ({ showToast }) => {
  const [branches, setBranches] = useState([
    { id: 1, name: 'Lighting', breaker: 20, load: 16, phase: 'A' },
    { id: 2, name: 'Outlets', breaker: 20, load: 16, phase: 'B' },
  ]);
  const [mainVoltage, setMainVoltage] = useState(230);
  const [demandFactor, setDemandFactor] = useState(1.0);

  const update = (id, field, value) =>
    setBranches((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));

  const addBranch = () =>
    setBranches((prev) => [...prev, { id: Date.now(), name: '', breaker: 20, load: 0, phase: 'A' }]);

  const removeBranch = (id) =>
    setBranches((prev) => (prev.length === 1 ? prev : prev.filter((b) => b.id !== id)));

  const result = useMemo(() => {
    let totalLoad = 0;
    let maxBreaker = 0;
    const perPhase = { A: 0, B: 0, C: 0 };
    const branchDetails = branches.map((b) => {
      const l = Number(b.load) || 0;
      totalLoad += l;
      maxBreaker = Math.max(maxBreaker, Number(b.breaker) || 0);
      perPhase[b.phase] = (perPhase[b.phase] || 0) + l;
      const utilization = b.breaker > 0 ? (l / b.breaker) * 100 : 0;
      const va = mainVoltage * l;
      return { ...b, va, utilization };
    });
    const totalVA = mainVoltage * totalLoad;
    const demandLoad = totalLoad * demandFactor;
    const demandVA = mainVoltage * demandLoad;
    const imbalance = Math.max(...Object.values(perPhase)) - Math.min(...Object.values(perPhase));
    const avgPhase = (perPhase.A + perPhase.B + perPhase.C) / 3;
    const imbalancePct = avgPhase > 0 ? (imbalance / avgPhase) * 100 : 0;
    const recommendedMain = BREAKER_SIZES.find((s) => s >= demandLoad * 1.25) || null;
    const kw = demandVA / 1000;

    return {
      totalLoad, demandLoad, totalVA, demandVA, maxBreaker,
      perPhase, imbalance, imbalancePct, recommendedMain,
      branchDetails, kw,
    };
  }, [branches, mainVoltage, demandFactor]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Panel Settings" icon="fa-sliders-h">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <NumberInput label="Main Voltage" value={mainVoltage} onChange={setMainVoltage} min={1} suffix="V" />
            <NumberInput label="Demand Factor" value={demandFactor} onChange={setDemandFactor} min={0.1} max={1} step={0.05} />
          </div>
        </SectionCard>

        <SectionCard title="Branch Circuits" icon="fa-table-cells-large">
          <div className="space-y-3">
            <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400 px-1">
              <div className="col-span-4">Circuit Name</div>
              <div className="col-span-2">Breaker (A)</div>
              <div className="col-span-3">Load (A)</div>
              <div className="col-span-2">Phase</div>
              <div className="col-span-1 text-right">–</div>
            </div>
            <AnimatePresence initial={false}>
              {branches.map((b, idx) => (
                <motion.div
                  key={b.id}
                  layout
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="grid grid-cols-2 md:grid-cols-12 gap-2 items-end bg-gray-50 dark:bg-gray-700/40 rounded-lg p-2"
                >
                  <div className="col-span-2 md:col-span-4">
                    <TextInput label="Name" value={b.name} onChange={(v) => update(b.id, 'name', v)} placeholder={`Circuit ${idx + 1}`} />
                  </div>
                  <div className="md:col-span-2">
                    <NumberInput label="Breaker" value={b.breaker} onChange={(v) => update(b.id, 'breaker', v)} min={1} suffix="A" />
                  </div>
                  <div className="md:col-span-3">
                    <NumberInput label="Load" value={b.load} onChange={(v) => update(b.id, 'load', v)} min={0} step={0.5} suffix="A" />
                  </div>
                  <div className="md:col-span-2">
                    <SelectInput label="Phase" value={b.phase} onChange={(v) => update(b.id, 'phase', v)} options={['A', 'B', 'C']} />
                  </div>
                  <div className="md:col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeBranch(b.id)}
                      disabled={branches.length === 1}
                      className="text-red-500 hover:bg-red-500/10 p-1.5 rounded-full transition disabled:opacity-30"
                      aria-label="Remove branch"
                    >
                      <i className="fas fa-trash-alt text-xs" aria-hidden="true" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          <button type="button" onClick={addBranch} className="mt-4 bg-orange hover:bg-orange-dark text-white px-4 py-2 rounded-lg text-sm font-semibold transition">
            <i className="fas fa-plus mr-1" aria-hidden="true" /> Add Circuit
          </button>
        </SectionCard>

        <SectionCard title="Branch Detail" icon="fa-list-ul">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2">Circuit</th>
                  <th className="py-2">Phase</th>
                  <th className="py-2 text-right">Breaker</th>
                  <th className="py-2 text-right">Load</th>
                  <th className="py-2 text-right">VA</th>
                  <th className="py-2 text-right">Utilization</th>
                </tr>
              </thead>
              <tbody>
                {result.branchDetails.map((b) => (
                  <tr key={b.id} className="border-b border-gray-100 dark:border-gray-700/50">
                    <td className="py-2 font-medium">{b.name || 'Unnamed'}</td>
                    <td className="py-2">{b.phase}</td>
                    <td className="py-2 text-right">{b.breaker} A</td>
                    <td className="py-2 text-right">{b.load} A</td>
                    <td className="py-2 text-right">{b.va.toFixed(0)}</td>
                    <td className={`py-2 text-right font-semibold ${b.utilization > 80 ? 'text-red-500' : b.utilization > 60 ? 'text-yellow-600' : 'text-green-600 dark:text-green-400'}`}>
                      {b.utilization.toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <i className="fas fa-table-cells-large text-orange" aria-hidden="true" /> Result
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <ResultStat label="Total Load" value={result.totalLoad.toFixed(1)} unit="A" icon="fa-bolt" />
              <ResultStat label="Demand Load" value={result.demandLoad.toFixed(1)} unit="A" icon="fa-gauge" highlight />
              <ResultStat label="Apparent Power" value={(result.demandVA / 1000).toFixed(2)} unit="kVA" icon="fa-plug" />
              <ResultStat label="Largest Branch" value={result.maxBreaker} unit="A" icon="fa-toggle-on" />
            </div>
            <div className="mt-4 text-xs text-gray-600 dark:text-gray-300 space-y-1">
              <div className="flex justify-between"><span>Phase A</span><span className="font-semibold">{result.perPhase.A.toFixed(1)} A</span></div>
              <div className="flex justify-between"><span>Phase B</span><span className="font-semibold">{result.perPhase.B.toFixed(1)} A</span></div>
              <div className="flex justify-between"><span>Phase C</span><span className="font-semibold">{result.perPhase.C.toFixed(1)} A</span></div>
              <div className="flex justify-between text-orange"><span>Imbalance</span><span className="font-semibold">{result.imbalance.toFixed(1)} A ({result.imbalancePct.toFixed(1)}%)</span></div>
            </div>
            {result.recommendedMain && (
              <div className="mt-4 p-3 rounded-xl bg-orange/10 border border-orange/20 text-sm">
                <div className="text-xs text-gray-600 dark:text-gray-300 mb-1">Recommended Main Breaker</div>
                <div className="text-2xl font-bold text-orange">{result.recommendedMain} A</div>
              </div>
            )}

            <DetailsPanel title="Full Calculation Details">
              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Loading</div>
              <DetailRow label="Connected load" value={result.totalLoad.toFixed(1)} unit="A" />
              <DetailRow label="Demand factor" value={demandFactor.toFixed(2)} unit="×" />
              <DetailRow label="Demand load" value={result.demandLoad.toFixed(1)} unit="A" />
              <DetailRow label="Apparent power" value={(result.demandVA / 1000).toFixed(2)} unit="kVA" />
              <DetailRow label="Real power (PF=1)" value={result.kw.toFixed(2)} unit="kW" />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Balance</div>
              <DetailRow label="Phase A" value={result.perPhase.A.toFixed(1)} unit="A" />
              <DetailRow label="Phase B" value={result.perPhase.B.toFixed(1)} unit="A" />
              <DetailRow label="Phase C" value={result.perPhase.C.toFixed(1)} unit="A" />
              <DetailRow label="Max imbalance" value={result.imbalance.toFixed(1)} unit="A" />
              <DetailRow label="Imbalance %" value={result.imbalancePct.toFixed(1)} unit="%" />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Main breaker</div>
              <DetailRow label="Demand × 1.25" value={(result.demandLoad * 1.25).toFixed(1)} unit="A" />
              <DetailRow label="Next standard" value={result.recommendedMain || 'N/A'} unit="A" />
            </DetailsPanel>

            <FormulaBlock
              title="Main breaker sizing"
              formula={`I_main ≥ I_demand × 1.25\nI_main ≥ ${result.demandLoad.toFixed(1)} × 1.25 = ${(result.demandLoad * 1.25).toFixed(1)} A\n→ ${result.recommendedMain || 'N/A'} A standard size`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool: Ohm's Law                                                     */
/* ================================================================== */

const OhmsLawTool = ({ showToast }) => {
  const [solveFor, setSolveFor] = useState('I');
  const [v, setV] = useState(230);
  const [i, setI] = useState('');
  const [r, setR] = useState('');
  const [p, setP] = useState('');

  const result = useMemo(() => {
    const V = Number(v) || 0;
    const I = Number(i) || 0;
    const R = Number(r) || 0;
    const P = Number(p) || 0;

    let out = { V, I, R, P };
    try {
      switch (solveFor) {
        case 'I':
          if (V && R) out.I = V / R;
          else if (P && V) out.I = P / V;
          else if (P && R) out.I = Math.sqrt(P / R);
          break;
        case 'V':
          if (I && R) out.V = I * R;
          else if (P && I) out.V = P / I;
          else if (P && R) out.V = Math.sqrt(P * R);
          break;
        case 'R':
          if (V && I) out.R = V / I;
          else if (V && P) out.R = (V * V) / P;
          else if (P && I) out.R = P / (I * I);
          break;
        case 'P':
          if (V && I) out.P = V * I;
          else if (I && R) out.P = I * I * R;
          else if (V && R) out.P = (V * V) / R;
          break;
        default:
          break;
      }
    } catch (e) {
      // ignore
    }
    return out;
  }, [solveFor, v, i, r, p]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Inputs" icon="fa-square-root-variable">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectInput label="Solve For" value={solveFor} onChange={setSolveFor} options={[{ value: 'V', label: 'Voltage (V)' }, { value: 'I', label: 'Current (I)' }, { value: 'R', label: 'Resistance (R)' }, { value: 'P', label: 'Power (P)' }]} />
            <div />
            {solveFor !== 'V' && (<NumberInput label="Voltage (V)" value={v} onChange={setV} min={0} suffix="V" />)}
            {solveFor !== 'I' && (<NumberInput label="Current (A)" value={i} onChange={setI} min={0} suffix="A" />)}
            {solveFor !== 'R' && (<NumberInput label="Resistance (Ω)" value={r} onChange={setR} min={0} step={0.1} suffix="Ω" />)}
            {solveFor !== 'P' && (<NumberInput label="Power (W)" value={p} onChange={setP} min={0} suffix="W" />)}
          </div>
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Enter any two values to solve for the third/fourth.
          </p>
        </SectionCard>

        <SectionCard title="All Ohm's Law Formulas" icon="fa-book">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {[
              { name: 'Voltage from I, R', f: 'V = I × R' },
              { name: 'Voltage from P, I', f: 'V = P ÷ I' },
              { name: 'Voltage from P, R', f: 'V = √(P × R)' },
              { name: 'Current from V, R', f: 'I = V ÷ R' },
              { name: 'Current from P, V', f: 'I = P ÷ V' },
              { name: 'Current from P, R', f: 'I = √(P ÷ R)' },
              { name: 'Resistance from V, I', f: 'R = V ÷ I' },
              { name: 'Resistance from V, P', f: 'R = V² ÷ P' },
              { name: 'Resistance from P, I', f: 'R = P ÷ I²' },
              { name: 'Power from V, I', f: 'P = V × I' },
              { name: 'Power from I, R', f: 'P = I² × R' },
              { name: 'Power from V, R', f: 'P = V² ÷ R' },
            ].map((row) => (
              <div key={row.name} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-700/40">
                <span className="text-gray-600 dark:text-gray-300">{row.name}</span>
                <span className="font-mono font-bold text-orange">{row.f}</span>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <i className="fas fa-square-root-variable text-orange" aria-hidden="true" /> Result
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <ResultStat label="Voltage" value={Number(result.V || 0).toFixed(2)} unit="V" icon="fa-bolt" />
              <ResultStat label="Current" value={Number(result.I || 0).toFixed(3)} unit="A" icon="fa-wave-square" />
              <ResultStat label="Resistance" value={Number(result.R || 0).toFixed(3)} unit="Ω" icon="fa-resistance" />
              <ResultStat label="Power" value={Number(result.P || 0).toFixed(2)} unit="W" icon="fa-plug" highlight />
            </div>

            <DetailsPanel title="Extra Values">
              <DetailRow label="Power (kW)" value={(Number(result.P || 0) / 1000).toFixed(4)} unit="kW" />
              <DetailRow label="Current (mA)" value={(Number(result.I || 0) * 1000).toFixed(1)} unit="mA" />
              <DetailRow label="Resistance (kΩ)" value={(Number(result.R || 0) / 1000).toFixed(4)} unit="kΩ" />
            </DetailsPanel>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool: Motor FLA                                                     */
/* ================================================================== */

const MOTOR_FLA_TABLE = {
  '0.5': { '1ph230': 4.9, '3ph230': 2.2, '3ph460': 1.1 },
  '0.75': { '1ph230': 6.9, '3ph230': 3.0, '3ph460': 1.5 },
  '1': { '1ph230': 8.0, '3ph230': 3.6, '3ph460': 1.8 },
  '1.5': { '1ph230': 10.0, '3ph230': 5.2, '3ph460': 2.6 },
  '2': { '1ph230': 12.0, '3ph230': 6.8, '3ph460': 3.4 },
  '3': { '1ph230': null, '3ph230': 9.6, '3ph460': 4.8 },
  '5': { '1ph230': null, '3ph230': 15.2, '3ph460': 7.6 },
  '7.5': { '1ph230': null, '3ph230': 22, '3ph460': 11 },
  '10': { '1ph230': null, '3ph230': 28, '3ph460': 14 },
  '15': { '1ph230': null, '3ph230': 42, '3ph460': 21 },
  '20': { '1ph230': null, '3ph230': 54, '3ph460': 27 },
  '25': { '1ph230': null, '3ph230': 68, '3ph460': 34 },
  '30': { '1ph230': null, '3ph230': 80, '3ph460': 40 },
  '40': { '1ph230': null, '3ph230': 104, '3ph460': 52 },
  '50': { '1ph230': null, '3ph230': 130, '3ph460': 65 },
  '60': { '1ph230': null, '3ph230': 154, '3ph460': 77 },
  '75': { '1ph230': null, '3ph230': 192, '3ph460': 96 },
  '100': { '1ph230': null, '3ph230': 248, '3ph460': 124 },
};

const MotorFlaTool = ({ showToast }) => {
  const [hp, setHp] = useState('5');
  const [phase, setPhase] = useState('3ph230');

  const fla = MOTOR_FLA_TABLE[hp]?.[phase] ?? null;
  const breaker = fla ? BREAKER_SIZES.find((b) => b >= fla * 2.5) : null;
  const overload = fla ? fla * 1.15 : 0;
  const branchCircuit = fla ? fla * 1.25 : 0;
  const refRow = MOTOR_FLA_TABLE[hp] || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Motor Details" icon="fa-gear">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <SelectInput label="Motor Horsepower" value={hp} onChange={setHp} options={Object.keys(MOTOR_FLA_TABLE)} />
            <SelectInput label="Phase / Voltage" value={phase} onChange={setPhase} options={[{ value: '1ph230', label: '1-phase, 230 V' }, { value: '3ph230', label: '3-phase, 230 V' }, { value: '3ph460', label: '3-phase, 460 V' }]} />
          </div>
        </SectionCard>

        <SectionCard title={`FLA Reference for ${hp} HP`} icon="fa-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2">Phase / Voltage</th>
                  <th className="py-2 text-right">FLA (A)</th>
                  <th className="py-2 text-right">Breaker @ 250%</th>
                  <th className="py-2 text-right">Overload @ 115%</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { key: '1ph230', label: '1-ph, 230 V' },
                  { key: '3ph230', label: '3-ph, 230 V' },
                  { key: '3ph460', label: '3-ph, 460 V' },
                ].map((row) => {
                  const rowFla = refRow[row.key];
                  const br = rowFla ? BREAKER_SIZES.find((b) => b >= rowFla * 2.5) : null;
                  const ol = rowFla ? rowFla * 1.15 : 0;
                  const isSelected = row.key === phase;
                  return (
                    <tr key={row.key} className={`border-b border-gray-100 dark:border-gray-700/50 ${isSelected ? 'bg-orange/5' : ''}`}>
                      <td className="py-2 font-medium">
                        {row.label} {isSelected && <span className="text-orange text-[10px]">← selected</span>}
                      </td>
                      <td className="py-2 text-right">{rowFla ?? '—'}</td>
                      <td className="py-2 text-right">{br ?? '—'}</td>
                      <td className="py-2 text-right">{rowFla ? ol.toFixed(1) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <i className="fas fa-gear text-orange" aria-hidden="true" /> Result
            </h2>
            {fla ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <ResultStat label="Full Load Amps" value={fla} unit="A" icon="fa-bolt" highlight />
                  <ResultStat label="Overload (115%)" value={overload.toFixed(1)} unit="A" icon="fa-triangle-exclamation" />
                  <ResultStat label="Breaker (250%)" value={breaker || '—'} unit="A" icon="fa-toggle-on" />
                  <ResultStat label="Motor Size" value={hp} unit="HP" icon="fa-gear" />
                </div>
                <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                  Breaker sizing based on NEC 430.52 (250% of FLA for inverse-time breakers).
                </p>

                <DetailsPanel title="Full Calculation Details">
                  <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Motor</div>
                  <DetailRow label="Horsepower" value={hp} unit="HP" />
                  <DetailRow label="Phase/Voltage" value={phase.replace('ph', '-ph ')} />
                  <DetailRow label="FLA" value={fla} unit="A" />

                  <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Protection</div>
                  <DetailRow label="Branch circuit (125%)" value={branchCircuit.toFixed(2)} unit="A" />
                  <DetailRow label="Overload (115%)" value={overload.toFixed(2)} unit="A" />
                  <DetailRow label="Breaker (250%)" value={(fla * 2.5).toFixed(1)} unit="A" />
                  <DetailRow label="Standard breaker" value={breaker || '—'} unit="A" />
                </DetailsPanel>

                <FormulaBlock
                  title="NEC 430.52"
                  formula={`Breaker = FLA × 250%\n        = ${fla} × 2.5\n        = ${(fla * 2.5).toFixed(1)} A\n→ next standard: ${breaker} A`}
                />
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No FLA data for this combination. Select a different HP or phase.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool: Power Factor Correction                                       */
/* ================================================================== */

const PowerFactorTool = ({ showToast }) => {
  const [loadKw, setLoadKw] = useState(100);
  const [pf1, setPf1] = useState(0.75);
  const [pf2, setPf2] = useState(0.95);

  const result = useMemo(() => {
    const kw = Number(loadKw) || 0;
    const p1 = Math.max(0.01, Math.min(1, Number(pf1) || 0));
    const p2 = Math.max(0.01, Math.min(1, Number(pf2) || 0));
    const theta1 = Math.acos(p1);
    const theta2 = Math.acos(p2);
    const tan1 = Math.tan(theta1);
    const tan2 = Math.tan(theta2);
    const kvar1 = kw * tan1;
    const kvar2 = kw * tan2;
    const kvarNeeded = kvar1 - kvar2;
    const kva1 = kw / p1;
    const kva2 = kw / p2;
    const reduction = kva1 > 0 ? ((kva1 - kva2) / kva1) * 100 : 0;
    const savings = kva1 - kva2;

    return {
      kvar1, kvar2, kvarNeeded, kva1, kva2, reduction, savings,
      theta1, theta2, tan1, tan2, p1, p2, kw,
    };
  }, [loadKw, pf1, pf2]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <SectionCard title="Load Details" icon="fa-wave-square">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <NumberInput label="Real Power" value={loadKw} onChange={setLoadKw} min={0} suffix="kW" />
            <NumberInput label="Existing PF" value={pf1} onChange={setPf1} min={0.01} max={1} step={0.01} />
            <NumberInput label="Target PF" value={pf2} onChange={setPf2} min={0.01} max={1} step={0.01} />
          </div>
          <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Capacitor bank kVAr required = kW × (tan θ₁ − tan θ₂)
          </p>
        </SectionCard>

        <SectionCard title="Trigonometric Details" icon="fa-calculator">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2">Quantity</th>
                  <th className="py-2 text-right">Existing</th>
                  <th className="py-2 text-right">Target</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <td className="py-2">Power factor (cos θ)</td>
                  <td className="py-2 text-right">{result.p1.toFixed(3)}</td>
                  <td className="py-2 text-right">{result.p2.toFixed(3)}</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <td className="py-2">Angle θ (degrees)</td>
                  <td className="py-2 text-right">{((result.theta1 * 180) / Math.PI).toFixed(2)}°</td>
                  <td className="py-2 text-right">{((result.theta2 * 180) / Math.PI).toFixed(2)}°</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <td className="py-2">tan θ</td>
                  <td className="py-2 text-right">{result.tan1.toFixed(4)}</td>
                  <td className="py-2 text-right">{result.tan2.toFixed(4)}</td>
                </tr>
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <td className="py-2">Reactive power (kVAr)</td>
                  <td className="py-2 text-right">{result.kvar1.toFixed(2)}</td>
                  <td className="py-2 text-right">{result.kvar2.toFixed(2)}</td>
                </tr>
                <tr>
                  <td className="py-2">Apparent power (kVA)</td>
                  <td className="py-2 text-right">{result.kva1.toFixed(2)}</td>
                  <td className="py-2 text-right">{result.kva2.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>
      </div>

      <div className="lg:col-span-1">
        <div className="sticky top-6 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-5">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <i className="fas fa-wave-square text-orange" aria-hidden="true" /> Result
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <ResultStat label="Capacitor Size" value={result.kvarNeeded.toFixed(2)} unit="kVAr" icon="fa-bolt" highlight />
              <ResultStat label="kVA Reduction" value={result.reduction.toFixed(1)} unit="%" icon="fa-arrow-down" />
              <ResultStat label="Existing kVA" value={result.kva1.toFixed(2)} unit="kVA" icon="fa-plug" />
              <ResultStat label="Target kVA" value={result.kva2.toFixed(2)} unit="kVA" icon="fa-gauge" />
            </div>

            <DetailsPanel title="Full Calculation Details">
              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Reactive Power</div>
              <DetailRow label="kVAr existing" value={result.kvar1.toFixed(2)} unit="kVAr" />
              <DetailRow label="kVAr target" value={result.kvar2.toFixed(2)} unit="kVAr" />
              <DetailRow label="kVAr required" value={result.kvarNeeded.toFixed(2)} unit="kVAr" />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Apparent Power</div>
              <DetailRow label="kVA existing" value={result.kva1.toFixed(2)} unit="kVA" />
              <DetailRow label="kVA target" value={result.kva2.toFixed(2)} unit="kVA" />
              <DetailRow label="kVA reduction" value={result.savings.toFixed(2)} unit="kVA" />
              <DetailRow label="Percent reduction" value={result.reduction.toFixed(2)} unit="%" />

              <div className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase mt-3 mb-1">Angles</div>
              <DetailRow label="θ₁" value={((result.theta1 * 180) / Math.PI).toFixed(2)} unit="°" />
              <DetailRow label="θ₂" value={((result.theta2 * 180) / Math.PI).toFixed(2)} unit="°" />
              <DetailRow label="tan θ₁" value={result.tan1.toFixed(4)} />
              <DetailRow label="tan θ₂" value={result.tan2.toFixed(4)} />
            </DetailsPanel>

            <FormulaBlock
              title="Capacitor sizing"
              formula={`Q_c = P × (tan θ₁ − tan θ₂)\n    = ${result.kw} × (${result.tan1.toFixed(3)} − ${result.tan2.toFixed(3)})\n    = ${result.kvarNeeded.toFixed(2)} kVAr`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================== */
/* Tool Renderer                                                       */
/* ================================================================== */

const TOOL_COMPONENTS = {
  solar: SolarTool,
  oneLine: OneLineTool,
  voltageDrop: VoltageDropTool,
  cableSizing: CableSizingTool,
  breakerSizing: BreakerSizingTool,
  panelboard: PanelboardTool,
  ohmsLaw: OhmsLawTool,
  motorFla: MotorFlaTool,
  powerFactor: PowerFactorTool,
};

/* ================================================================== */
/* Main Toolkit Component                                              */
/* ================================================================== */

const Toolkit = () => {
  const [activeTool, setActiveTool] = useState(null);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const filteredTools = useMemo(() => {
    if (!search.trim()) return TOOLS;
    const q = search.toLowerCase();
    return TOOLS.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q),
    );
  }, [search]);

  const groupedTools = useMemo(() => {
    const groups = {};
    filteredTools.forEach((t) => {
      if (!groups[t.category]) groups[t.category] = [];
      groups[t.category].push(t);
    });
    return groups;
  }, [filteredTools]);

  const active = TOOLS.find((t) => t.id === activeTool);
  const ActiveComponent = activeTool ? TOOL_COMPONENTS[activeTool] : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 pt-8 pb-16">
      <AnimatePresence>
        {toast && (
          <Toast
            key="toast"
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <div className="container mx-auto px-4 max-w-6xl">
        {active ? (
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ToolHeader tool={active} onBack={() => setActiveTool(null)} />
            <ActiveComponent showToast={showToast} />
          </motion.div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-10">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-800 dark:text-white relative inline-block">
                EE Toolkit
                <span className="absolute -bottom-2 left-0 w-1/2 h-1 bg-orange rounded-full" />
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-3">
                A collection of electrical engineering calculators for design
                and field work
              </p>
            </div>

            {/* Search */}
            <div className="max-w-xl mx-auto mb-8">
              <div className="relative">
                <i
                  className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tools..."
                  className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange shadow-sm"
                />
              </div>
            </div>

            {/* Tool Grid grouped by category */}
            {filteredTools.length === 0 ? (
              <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                <i className="fas fa-search text-3xl mb-3 block opacity-50" aria-hidden="true" />
                No tools match "{search}"
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(groupedTools).map(([category, items]) => (
                  <div key={category}>
                    <h3 className="text-xs uppercase font-bold text-gray-400 dark:text-gray-500 tracking-wider mb-3 px-1">
                      {category}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {items.map((tool) => (
                        <motion.button
                          key={tool.id}
                          type="button"
                          onClick={() => setActiveTool(tool.id)}
                          whileHover={{ y: -4 }}
                          whileTap={{ scale: 0.98 }}
                          className="text-left bg-white dark:bg-gray-800 rounded-2xl shadow-md hover:shadow-xl p-5 transition-all border border-transparent hover:border-orange/30 focus:outline-none focus:ring-2 focus:ring-orange"
                        >
                          <div className="flex items-start gap-3 mb-3">
                            <div className={`w-12 h-12 rounded-xl ${tool.bg} flex items-center justify-center flex-shrink-0`}>
                              <i className={`fas ${tool.icon} ${tool.color} text-lg`} aria-hidden="true" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-bold text-gray-800 dark:text-white">
                                {tool.name}
                              </h3>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 leading-snug">
                            {tool.description}
                          </p>
                          <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-orange">
                            Open
                            <i className="fas fa-arrow-right text-[10px]" aria-hidden="true" />
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Toolkit;